import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { SAMPLE_ORDERS } from "../../lib/sampleData";
import { SamplePreviewBanner, SampleChip, SampleOrderDrawer } from "../../components/SamplePreview";
import { errorToastFromAxios } from "../../lib/supportToast";
import InvoiceExpiryChip from "../../components/InvoiceExpiryChip";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import SearchAndFilter from "../../components/SearchAndFilter";
import ClaimsTab from "../../components/ClaimsTab";
import AdjustInvoiceModal from "../../components/AdjustInvoiceModal";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Clock, Package, Truck, Check, FileText,
  CreditCard, Sparkles, Handshake, Download, Pencil, Plus, X,
  DollarSign, CalendarDays, User, Receipt, MapPin, AlertTriangle, Copy,
  Activity, Zap, RotateCcw,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// SellerOrders — fulfillment lifecycle for the seller.
// Mirrors the buyer orders page style (same pill-tab bar, KPI row, searchable
// table and Sheet drawer) so sellers have a consistent scannable view across
// all stages: Confirming · Awaiting payment · Ready to pack · In transit · Delivered.
// ─────────────────────────────────────────────────────────────────────────────

// Map order+invoice → seller-side stage
function getSellerStage(order, invoice) {
  if (order.status === "delivered") return "delivered";
  if (order.status === "transit") return "transit";
  // After buyer pays, the order is in "processing". The seller already
  // committed a ready date at confirm time, so we go straight to preparing
  // → ready_for_pickup once shipping info is entered.
  if (order.status === "ready_for_pickup" || order.ready_to_ship) return "ready_for_pickup";
  if (order.status === "processing") return "preparing";
  if (invoice && invoice.status !== "paid" && invoice.status !== "draft") return "awaiting_pay";
  return "confirm";
}

const STAGE_META = {
  confirm:           { label: "Awaiting confirmation", icon: Clock,      chip: "bg-amber-100 text-amber-800",    emoji: "⏳" },
  awaiting_pay:      { label: "Awaiting payment",      icon: CreditCard, chip: "bg-rose-100 text-rose-800",       emoji: "💳" },
  preparing:         { label: "Preparing order",       icon: Package,    chip: "bg-blue-100 text-blue-800",       emoji: "📦" },
  ready_for_pickup:  { label: "Ready for pickup",      icon: Truck,      chip: "bg-teal-100 text-teal-800",        emoji: "✅" },
  transit:           { label: "In transit",            icon: Truck,      chip: "bg-indigo-100 text-indigo-800",    emoji: "🚚" },
  delivered:         { label: "Delivered",             icon: Check,      chip: "bg-emerald-100 text-emerald-800",  emoji: "✓" },
};

// ─────────────────────────────────────────────────────────────────────────────
// LEAN-LAYOUT HELPERS (Feb 2026 redesign)
// ─────────────────────────────────────────────────────────────────────────────

// Compute the latest known lifecycle timestamp for an order. Heuristic
// for "age in current stage" — when an order transitions to a new stage,
// some collection (invoice / shipment / order itself) usually gets a
// fresh write, so MAX(all timestamps) approximates "stage entered at".
function stageEnteredAt(order) {
  const stamps = [
    order?.last_adjusted_at,
    order?.shipment?.delivered_at,
    order?.shipment?.shipped_at,
    order?.shipment?.created_at,
    order?.invoice?.paid_at,
    order?.invoice?.issued_at,
    order?.invoice?.created_at,
    order?.confirmed_at,
    order?.created_at,
  ].filter(Boolean).map(t => new Date(t).getTime()).filter(Number.isFinite);
  return stamps.length ? Math.max(...stamps) : null;
}

// "4d", "5h", "32m" — compact duration since stage transition.
function ageInStageLabel(order) {
  const t = stageEnteredAt(order);
  if (!t) return "—";
  const ms = Date.now() - t;
  if (ms < 60_000) return "now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

// SLA tint for the Action-needed tab. >24h amber, >48h rose.
function ageSlaTone(order, view) {
  const t = stageEnteredAt(order);
  if (!t) return null;
  const hrs = (Date.now() - t) / 3_600_000;
  if (view === "action") {
    if (hrs > 48) return "rose";
    if (hrs > 24) return "amber";
  } else if (view === "progress") {
    if (hrs > 24 * 10) return "rose";
    if (hrs > 24 * 5)  return "amber";
  }
  return null;
}

// 5 consolidated tabs (Feb 2026 redesign).
// Old hashes are preserved as legacy aliases that get auto-redirected to
// the new merged tabs in the redirect-effect below.
const VALID_VIEWS = [
  "all", "action", "progress", "delivered", "claims",
  // legacy aliases (auto-redirected)
  "confirm", "awaiting_pay", "preparing", "ready_for_pickup", "transit",
];
const VIEW_LABEL = {
  all:       "All orders",
  action:    "Action needed",
  progress:  "In progress",
  delivered: "Delivered",
  claims:    "Claims",
};
// Map each view to the underlying stages it includes
const VIEW_STAGES = {
  action:    ["confirm", "preparing"],
  progress:  ["awaiting_pay", "ready_for_pickup", "transit"],
  delivered: ["delivered"],
};
const LEGACY_VIEW_REMAP = {
  confirm: "action",       preparing: "action",
  awaiting_pay: "progress", ready_for_pickup: "progress", transit: "progress",
};

const VIEW_TABS = [
  { key: "all",       label: "All",            icon: FileText,      countKey: "all" },
  { key: "action",    label: "Action needed",  icon: Zap,           countKey: "action",    tone: "amber",   action: true },
  { key: "progress",  label: "In progress",    icon: Activity,      countKey: "progress",  tone: "blue" },
  { key: "delivered", label: "Delivered",      icon: Check,         countKey: "delivered", tone: "emerald" },
  { key: "claims",    label: "Claims",         icon: AlertTriangle, countKey: "claims",    tone: "rose" },
];

export default function SellerOrders() {
  const nav = useNavigate();
  const loc = useLocation();
  const view = useMemo(() => {
    const h = (loc.hash || "").replace("#", "");
    return VALID_VIEWS.includes(h) ? h : "all";
  }, [loc.hash]);

  // Legacy hash redirect — old links like /seller/orders#preparing quietly
  // land on the merged "Action needed" / "In progress" tab.
  useEffect(() => {
    const h = (loc.hash || "").replace("#", "");
    if (LEGACY_VIEW_REMAP[h]) {
      nav(`/seller/orders#${LEGACY_VIEW_REMAP[h]}`, { replace: true });
    }
  }, [loc.hash, nav]);

  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [connections, setConnections] = useState([]); // connected buyers (for manual order entry)
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState(null);  // full order detail from /orders/{id}
  const [confirming, setConfirming] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);  // invoice doc to adjust
  const [filters, setFilters] = useState({});   // composable facet filters

  // Manual order entry — sellers key in phone / trade-show / sales-rep orders.
  const blankLine = () => ({ name: "", qty: 1, price: "" });
  const [creating, setCreating] = useState(false);
  const [newOrder, setNewOrder] = useState({
    buyer_id: "", terms: "net30", note: "", lines: [blankLine()],
  });
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const newOrderTotal = useMemo(
    () => newOrder.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price) || 0), 0),
    [newOrder.lines],
  );

  // Bulk paste mode — sales reps re-key trade-show orders into a sheet,
  // paste here, get N orders auto-grouped by buyer + terms.
  const [orderMode, setOrderMode] = useState("single"); // "single" | "bulk"
  const [bulkText, setBulkText] = useState("");
  const [bulkParsed, setBulkParsed] = useState(null);   // { rows, errors, groups }
  const [submittingBulk, setSubmittingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);   // server response after submit
  // Bulk-actions selection state — set of order IDs the seller has ticked.
  // Auto-clears when the view tab or filters change so a stale selection
  // can never trigger a confirm/remind on a row the user can no longer see.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Tenant-level operational defaults (e.g. production lead time → pre-fills
  // the "Set ready date" dialog so the seller doesn't re-type the same N days
  // for every paid order).
  const [defaultLeadDays, setDefaultLeadDays] = useState(null);
  const [offersPrebook, setOffersPrebook] = useState(false);
  // Convert-to-prebook modal state — opens from the order drawer when the
  // seller has prebook enabled and the order is still pending.
  const [pbOpen, setPbOpen] = useState(false);
  const [pbOrder, setPbOrder] = useState(null);
  const [pbDepositPct, setPbDepositPct] = useState(30);
  const [pbLeadWeeks, setPbLeadWeeks] = useState(8);
  const [pbSubmitting, setPbSubmitting] = useState(false);
  // Extension request modal state (for overdue prebooks).
  const [extOpen, setExtOpen] = useState(false);
  const [extOrder, setExtOrder] = useState(null);
  const [extDate, setExtDate] = useState("");
  const [extReason, setExtReason] = useState("");
  const [extSubmitting, setExtSubmitting] = useState(false);

  const [initialLoaded, setInitialLoaded] = useState(false);
  const load = () => Promise.all([
    api.get("/orders"), api.get("/invoices"), api.get("/shipping"),
    api.get("/tenant/connections").catch(() => ({ data: [] })),
    api.get("/tenant/me").catch(() => ({ data: null })),
  ]).then(([o, i, s, c, t]) => {
    setOrders(o.data); setInvoices(i.data); setShipments(s.data);
    setConnections(c.data || []);
    const lead = t?.data?.settings?.default_lead_days;
    setDefaultLeadDays(typeof lead === "number" ? lead : null);
    setOffersPrebook(!!((t?.data?.settings?.profile || {}).offers_prebook));
  }).finally(() => setInitialLoaded(true));
  useEffect(() => { load(); }, []);
  // Reset pagination on view/search/filter change
  useEffect(() => { setPage(1); }, [view, search, filters]);
  // Reset multi-select whenever the visible scope changes
  useEffect(() => { setSelected(new Set()); }, [view, search, filters]);

  const invoiceFor = (oid) => invoices.find(i => i.order_id === oid);
  const shipmentFor = (oid) => shipments.find(s => s.order_id === oid);

  const staged = useMemo(() => orders.map(o => {
    const inv = invoiceFor(o.id);
    const sh = shipmentFor(o.id);
    return { ...o, invoice: inv, shipment: sh, stage: getSellerStage(o, inv) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [orders, invoices, shipments]);

  const counts = useMemo(() => {
    const c = {
      all: staged.length, confirm: 0, awaiting_pay: 0,
      preparing: 0, ready_for_pickup: 0,
      transit: 0, delivered: 0,
    };
    staged.forEach(o => { c[o.stage] = (c[o.stage] || 0) + 1; });
    // Merged-tab counts — single source of truth for top tabs + sidebar.
    c.action   = c.confirm + c.preparing;
    c.progress = c.awaiting_pay + c.ready_for_pickup + c.transit;
    return c;
  }, [staged]);

  // Unique buyers across this seller's orders — powers the Buyer facet.
  const buyerOptions = useMemo(() => {
    const seen = new Map();
    staged.forEach(o => {
      if (o.buyer_id && !seen.has(o.buyer_id)) seen.set(o.buyer_id, o.buyer_name || o.buyer_id);
    });
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [staged]);

  const viewFiltered = useMemo(() => {
    let r = staged;
    const stageSet = VIEW_STAGES[view];
    if (stageSet) r = r.filter(o => stageSet.includes(o.stage));
    if (search) r = r.filter(o => JSON.stringify(o).toLowerCase().includes(search.toLowerCase()));

    // ── Composable facet filter overlay ─────────────────────────────────
    // filters shape: { status: { values }, buyer: { values }, terms: { values },
    //   total: { op, value | min, max }, date: { op, from, to, value },
    //   overdue: { on }, hasTracking: { on }, financed: { on } }
    const F = filters || {};

    if (F.status?.values?.length) r = r.filter(o => F.status.values.includes(o.stage));
    if (F.buyer?.values?.length)  r = r.filter(o => F.buyer.values.includes(o.buyer_id));
    if (F.terms?.values?.length)  r = r.filter(o => F.terms.values.includes(o.invoice?.terms || o.terms));

    if (F.total) {
      r = r.filter(o => {
        const t = o.total || 0;
        if (F.total.op === "gt") return t > F.total.value;
        if (F.total.op === "lt") return t < F.total.value;
        if (F.total.op === "between") return t >= F.total.min && t <= F.total.max;
        return true;
      });
    }
    if (F.date) {
      r = r.filter(o => {
        const d = new Date(o.created_at).getTime();
        if (F.date.op === "last7")  return d >= Date.now() - 7 * 86400000;
        if (F.date.op === "last30") return d >= Date.now() - 30 * 86400000;
        if (F.date.op === "before") return d < new Date(F.date.value).getTime();
        if (F.date.op === "after")  return d > new Date(F.date.value).getTime();
        if (F.date.op === "between") return d >= new Date(F.date.from).getTime() && d <= new Date(F.date.to).getTime() + 86400000;
        return true;
      });
    }
    if (F.overdue?.on != null) {
      const want = F.overdue.on;
      r = r.filter(o => {
        const inv = o.invoice;
        const overdue = !!inv && inv.status !== "paid" && inv.status !== "draft" &&
          (inv.due_date || inv.due_at) && new Date(inv.due_date || inv.due_at).getTime() < Date.now();
        return want ? overdue : !overdue;
      });
    }
    if (F.hasTracking?.on != null) {
      const want = F.hasTracking.on;
      r = r.filter(o => want ? !!o.shipment?.tracking : !o.shipment?.tracking);
    }

    return r.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [staged, view, search, filters]);

  const PER_PAGE = 12;
  const totalPages = Math.max(1, Math.ceil(viewFiltered.length / PER_PAGE));
  const pageData = viewFiltered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ── Bulk-action helpers (computed against the current page) ────────────────
  const pageIds = useMemo(() => pageData.map(o => o.id), [pageData]);
  const allChecked = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const someChecked = !allChecked && pageIds.some(id => selected.has(id));
  const toggleOne = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const togglePage = () => setSelected(prev => {
    const n = new Set(prev);
    if (allChecked) pageIds.forEach(id => n.delete(id));
    else pageIds.forEach(id => n.add(id));
    return n;
  });
  // Selected orders, hydrated from the staged list, restricted to the
  // currently-filtered scope so we never act on hidden data.
  const selectedOrders = useMemo(
    () => viewFiltered.filter(o => selected.has(o.id)),
    [viewFiltered, selected],
  );
  const selectedConfirmable = selectedOrders.filter(o => o.stage === "confirm");
  const selectedRemindable = selectedOrders.filter(
    o => o.invoice && !["paid", "draft", "cancelled"].includes(o.invoice.status),
  );

  const bulkConfirm = async () => {
    const ids = selectedConfirmable.map(o => o.id);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/seller/orders/bulk-confirm", { order_ids: ids });
      const c = data?.confirmed_count ?? 0;
      const f = data?.failed_count ?? 0;
      if (c) toast.success(`Confirmed ${c} order${c === 1 ? "" : "s"}${f ? ` · ${f} skipped` : ""}`);
      else toast.error("Nothing was confirmed — see seller orders for details");
      setSelected(new Set());
      load();
    } catch (e) {
      errorToastFromAxios(e, "Bulk confirm failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkRemind = async () => {
    const ids = selectedRemindable.map(o => o.id);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/seller/invoices/bulk-remind", { order_ids: ids });
      const r = data?.reminded_count ?? 0;
      const f = data?.failed_count ?? 0;
      if (r) toast.success(`Reminder sent for ${r} invoice${r === 1 ? "" : "s"}${f ? ` · ${f} skipped` : ""}`);
      else toast.error("Nothing was reminded — see seller orders for details");
      setSelected(new Set());
      load();
    } catch (e) {
      errorToastFromAxios(e, "Bulk reminder failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Per-row reminder (used by the inline "Send reminder" button on the
  // In-progress tab). Reuses the bulk endpoint with a single id.
  const sendOneReminder = async (orderId) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/seller/invoices/bulk-remind", { order_ids: [orderId] });
      if (data?.reminded_count) toast.success("Reminder sent");
      else toast.error("Reminder could not be sent (already paid or skipped)");
      load();
    } catch (e) {
      errorToastFromAxios(e, "Reminder failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Pack-list PDF — server-side render that includes the seller's
  // published trade terms (QI window, returns, default lead, payment
  // terms, custom T&C). Replaces the old client-side CSV so warehouses
  // and buyers see consistent terms on every shipped doc.
  const bulkPackList = async () => {
    if (!selectedOrders.length) return;
    setBulkBusy(true);
    try {
      const res = await api.post(
        "/orders/pack-list/pdf",
        { order_ids: selectedOrders.map(o => o.id) },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pack-list-${selectedOrders.length}-orders-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Pack list PDF · ${selectedOrders.length} order${selectedOrders.length === 1 ? "" : "s"}`);
    } catch (e) {
      errorToastFromAxios(e, "Pack list download failed");
    } finally {
      setBulkBusy(false);
    }
  };

  // Per-view KPI tiles — mirrors buyer layout
  const viewStats = useMemo(() => {
    const sumBy = (stage, field = "total") =>
      staged.filter(o => o.stage === stage).reduce((s, o) => s + (field === "invoice_amount" ? (o.invoice?.amount || 0) : (o[field] || 0)), 0);
    const tiles = {
      all: [
        { l: "Total orders",        v: counts.all },
        { l: "Action needed",       v: counts.action },
        { l: "In progress",         v: counts.progress },
        { l: "Delivered",           v: counts.delivered },
      ],
      action: [
        { l: "Action needed",        v: counts.action },
        { l: "Total value",          v: money(sumBy("confirm") + sumBy("preparing")), big: true },
        { l: "Awaiting confirm",     v: counts.confirm },
        { l: "Preparing",            v: counts.preparing },
      ],
      progress: [
        { l: "In progress",       v: counts.progress },
        { l: "Total value",       v: money(sumBy("awaiting_pay", "invoice_amount") + sumBy("ready_for_pickup") + sumBy("transit")), big: true },
        { l: "Awaiting payment",  v: counts.awaiting_pay },
        { l: "Ready · transit",   v: counts.ready_for_pickup + counts.transit },
      ],
      delivered: [
        { l: "Delivered",     v: counts.delivered },
        { l: "Total shipped", v: money(sumBy("delivered")), big: true },
        { l: "Unique buyers", v: new Set(staged.filter(o => o.stage === "delivered").map(o => o.buyer_id)).size },
        { l: "On-time rate",  v: "97%" },
      ],
    };
    return tiles[view] || tiles.all;
  }, [staged, counts, view]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const openOrder = async (oid) => {
    try {
      const { data } = await api.get(`/orders/${oid}`);
      setDrawer(data);
    } catch { toast.error("Could not load order details"); }
  };

  // Drawer "Download PO" — renders a branded PO PDF for the on-platform
  // order. Works for any stage (pending, prepping, shipped, delivered)
  // since the PO is the buyer-issued document, not the invoice.
  const downloadOrderPo = async (o) => {
    try {
      const { data } = await api.get(`/orders/${o.id}/po.pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `PO-${o.po_number || o.code || o.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      errorToastFromAxios(e, "Could not download PO");
    }
  };

  // Drawer "Download Invoice" — same endpoint the Orders-table InvoiceCell
  // uses (kept consistent so the file naming + binary is identical).
  const downloadOrderInvoice = async (invoice) => {
    if (!invoice?.id) return;
    try {
      const { data, headers } = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `invoice-${invoice.code || invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      errorToastFromAxios(e, "Could not download invoice");
    }
  };

  const [confirmReadyDate, setConfirmReadyDate] = useState("");
  const openConfirm = (o) => {
    setConfirming(o);
    // Pre-fill ready date from the seller's default lead time (Settings →
    // Operations). Falls back to 5 days. Existing date wins if present.
    const fallbackDays = typeof defaultLeadDays === "number" ? defaultLeadDays : 5;
    setConfirmReadyDate(
      o.expected_ready_at
        ? new Date(o.expected_ready_at).toISOString().slice(0, 10)
        : new Date(Date.now() + fallbackDays * 86400000).toISOString().slice(0, 10)
    );
  };

  const submitConfirm = async () => {
    if (!confirmReadyDate) return toast.error("Pick a ready-by date");
    try {
      await api.post(`/orders/${confirming.id}/confirm`, {
        expected_invoice_at: new Date().toISOString(),
        expected_ready_at:   new Date(confirmReadyDate).toISOString(),
      });
      toast.success(`Order ${confirming.code} confirmed · ready by ${confirmReadyDate}`);
      setConfirming(null);
      setConfirmReadyDate("");
      setDrawer(null);
      load();
    } catch (e) {
      errorToastFromAxios(e, "Confirm failed", {
        subject: `Couldn't confirm order ${confirming.code}`,
        body:
          `I tried to confirm order ${confirming.code} (${confirming.id}) and got an error.\n` +
          `Buyer: ${confirming.buyer_name || confirming.buyer_id || "n/a"}\n` +
          `Ready-by: ${confirmReadyDate}`,
        urgency: "high",
        category: "orders",
      });
    }
  };

  // ── Convert PO → Prebook (seller side) ────────────────────────────────
  const openPrebook = (o) => {
    setPbOrder(o);
    setPbDepositPct(30);
    setPbLeadWeeks(8);
    setPbOpen(true);
  };
  const submitPrebook = async () => {
    if (!pbOrder) return;
    if (pbDepositPct <= 0 || pbDepositPct > 100) return toast.error("Deposit % must be 1–100");
    if (pbLeadWeeks < 1 || pbLeadWeeks > 52) return toast.error("Lead time must be 1–52 weeks");
    setPbSubmitting(true);
    try {
      await api.post(`/orders/${pbOrder.id}/prebook/convert`, {
        deposit_pct: Number(pbDepositPct),
        lead_time_weeks: Number(pbLeadWeeks),
      });
      toast.success(`Prebook offer sent to ${pbOrder.buyer_name || "buyer"}`);
      setPbOpen(false);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create prebook offer.");
    } finally { setPbSubmitting(false); }
  };

  // ── Extension request (seller side, on overdue prebook) ───────────────
  const openExtension = (o) => {
    setExtOrder(o);
    // Default new date = today + 2 weeks
    setExtDate(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));
    setExtReason("");
    setExtOpen(true);
  };
  const submitExtension = async () => {
    if (!extOrder) return;
    if (!extDate) return toast.error("Pick a new ready-by date");
    if (!extReason.trim() || extReason.trim().length < 3) return toast.error("Please explain the delay");
    setExtSubmitting(true);
    try {
      await api.post(`/orders/${extOrder.id}/prebook/request-extension`, {
        new_promised_date: new Date(extDate).toISOString(),
        reason: extReason.trim(),
      });
      toast.success("Extension request sent — buyer has 10 days to respond.");
      setExtOpen(false);
      const { data } = await api.get(`/orders/${extOrder.id}`);
      setDrawer(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request extension.");
    } finally { setExtSubmitting(false); }
  };
  const [readyDateOrder, setReadyDateOrder] = useState(null);
  const [readyDateValue, setReadyDateValue] = useState("");
  const openSetReadyDate = (o) => {
    setReadyDateOrder(o);
    // Pre-fill priority: existing ready date → tenant default lead days → 5d fallback.
    const fallbackDays = typeof defaultLeadDays === "number" ? defaultLeadDays : 5;
    setReadyDateValue(
      o.expected_ready_at
        ? new Date(o.expected_ready_at).toISOString().slice(0, 10)
        : new Date(Date.now() + fallbackDays * 86400000).toISOString().slice(0, 10)
    );
  };
  const submitReadyDate = async () => {
    if (!readyDateValue) return toast.error("Pick a ready date");
    try {
      await api.patch(`/orders/${readyDateOrder.id}/ready-date`, {
        expected_ready_at: new Date(readyDateValue).toISOString(),
      });
      toast.success(`Buyer notified · ready by ${readyDateValue}`);
      setReadyDateOrder(null);
      setReadyDateValue("");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  // Enter shipping info & flip ready-to-ship
  const [shipInfoOrder, setShipInfoOrder] = useState(null);
  const [shipInfo, setShipInfo] = useState({
    pallet_count: "",
    package_count: 1, total_weight_lb: "", package_dimensions: "",
    pickup_location: "", pickup_window: "Mon–Fri 9am–5pm",
    pickup_notes: "", bol_number: "",
  });
  const openShippingInfo = async (o) => {
    const existing = o.shipping_info || {};
    setShipInfoOrder(o);
    // Seed sensible defaults: if the seller hasn't entered a pickup location
    // or window for THIS order yet, fall back to their saved default
    // warehouse address + the `default_pickup_window` from Operations
    // settings. Avoids re-typing the same address on every order.
    let defPickup = "";
    let defWindow = "Mon–Fri 9am–5pm";
    try {
      const [addrR, tenR] = await Promise.all([
        api.get("/addresses").catch(() => ({ data: [] })),
        api.get("/tenant/me").catch(() => ({ data: {} })),
      ]);
      const addrs = addrR.data || [];
      const def = addrs.find(a => a.is_default) || addrs[0];
      if (def) {
        defPickup = [def.line1, def.city, def.state, def.zip].filter(Boolean).join(", ");
      }
      const w = tenR.data?.settings?.default_pickup_window;
      if (w) defWindow = w;
    } catch { /* ignore — defaults already set */ }
    setShipInfo({
      pallet_count:         existing.pallet_count        ?? "",
      package_count:        existing.package_count       ?? 1,
      total_weight_lb:      existing.total_weight_lb     ?? "",
      package_dimensions:   existing.package_dimensions  ?? "",
      pickup_location:      existing.pickup_location     ?? defPickup,
      pickup_window:        existing.pickup_window       ?? defWindow,
      pickup_notes:         existing.pickup_notes        ?? "",
      bol_number:           existing.bol_number          ?? "",
    });
  };
  const submitShippingInfo = async () => {
    if (!shipInfo.total_weight_lb || Number(shipInfo.total_weight_lb) <= 0)
      return toast.error("Enter the total weight");
    if (!shipInfo.pickup_location.trim())
      return toast.error("Pickup location is required");
    try {
      await api.patch(`/orders/${shipInfoOrder.id}/shipping-info`, {
        ...shipInfo,
        package_count:    Number(shipInfo.package_count) || 1,
        total_weight_lb:  Number(shipInfo.total_weight_lb),
        pallet_count:     shipInfo.pallet_count !== "" ? Number(shipInfo.pallet_count) : null,
      });
      toast.success(`${shipInfoOrder.code} marked ready · buyer can schedule pickup`);
      setShipInfoOrder(null);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  // ── Release to buyer (Layer 2 of buyer-arranged shipping) ──────────────
  // Only valid for orders with shipping_mode='buyer_arranged'. Captures
  // recipient identity + photo proof + acknowledgment → generates the
  // legal release receipt PDF and moves the order to delivered.
  const [releaseOrder, setReleaseOrder] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    recipient_name: "", recipient_company: "", recipient_phone: "",
    signature_text: "", notes: "", photo_paths: [],
  });
  const [releaseSubmitting, setReleaseSubmitting] = useState(false);
  const openRelease = (o) => {
    setReleaseOrder(o);
    setReleaseForm({
      recipient_name: "", recipient_company: "", recipient_phone: "",
      signature_text: "", notes: "", photo_paths: [],
    });
  };
  const uploadReleasePhoto = async (file) => {
    if (!file) return;
    if (releaseForm.photo_paths.length >= 4) {
      return toast.error("Up to 4 photos per release receipt");
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/claims/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReleaseForm(f => ({ ...f, photo_paths: [...f.photo_paths, data.path] }));
      toast.success("Photo attached");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo upload failed");
    }
  };
  const submitRelease = async () => {
    if (!releaseForm.recipient_name.trim()) {
      return toast.error("Recipient name is required");
    }
    setReleaseSubmitting(true);
    try {
      await api.post(`/orders/${releaseOrder.id}/release`, {
        recipient_name:    releaseForm.recipient_name.trim(),
        recipient_company: releaseForm.recipient_company.trim() || null,
        recipient_phone:   releaseForm.recipient_phone.trim() || null,
        signature_text:    releaseForm.signature_text.trim() || null,
        notes:             releaseForm.notes.trim() || null,
        photo_paths:       releaseForm.photo_paths,
      });
      toast.success(`${releaseOrder.code} released · receipt on file`);
      setReleaseOrder(null);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release order");
    } finally {
      setReleaseSubmitting(false);
    }
  };

  // ── Manual order entry (phone / trade show / sales rep) ────────────────
  const openCreateOrder = () => {
    setNewOrder({ buyer_id: "", terms: "net30", note: "", lines: [blankLine()] });
    setOrderMode("single");
    setBulkText("");
    setBulkParsed(null);
    setBulkResult(null);
    setCreating(true);
  };
  const updateLine = (i, patch) => setNewOrder(o => ({
    ...o, lines: o.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l),
  }));
  const addLine = () => setNewOrder(o => ({ ...o, lines: [...o.lines, blankLine()] }));
  const removeLine = (i) => setNewOrder(o => ({
    ...o, lines: o.lines.length > 1 ? o.lines.filter((_, idx) => idx !== i) : o.lines,
  }));
  const submitNewOrder = async () => {
    if (!newOrder.buyer_id) return toast.error("Pick a buyer");
    const items = newOrder.lines
      .filter(l => l.name.trim() && Number(l.qty) > 0 && Number(l.price) >= 0)
      .map(l => ({
        product_id: "manual",
        name: l.name.trim(),
        qty: Number(l.qty),
        price: Number(l.price),
      }));
    if (items.length === 0) return toast.error("Add at least one line item");
    setSubmittingOrder(true);
    try {
      const { data } = await api.post("/seller/orders/manual", {
        buyer_id: newOrder.buyer_id,
        terms: newOrder.terms,
        note: newOrder.note || undefined,
        items,
      });
      toast.success(`Order ${data.order.code} created · invoice ${data.invoice.code} sent`);
      setCreating(false);
      await load();
      // Open the new order in the drawer so the seller can immediately set
      // a ready date or share the invoice.
      try {
        const detail = await api.get(`/orders/${data.order.id}`);
        setDrawer(detail.data);
      } catch { /* drawer open is best-effort */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create order");
    } finally {
      setSubmittingOrder(false);
    }
  };

  // ── Bulk paste (CSV / TSV) ─────────────────────────────────────────────
  // Auto-detect delimiter (tab if the header has a tab, else comma).
  // Supported headers (case-insensitive, any order):
  //   buyer_email | email · name | item | sku · qty | quantity · price | unit_price · terms · note
  const parseBulk = (raw) => {
    const text = (raw || "").trim();
    if (!text) {
      setBulkParsed({ rows: [], errors: ["Paste at least one row"], groups: [] });
      return;
    }
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length < 2) {
      setBulkParsed({ rows: [], errors: ["Need a header row + at least one data row"], groups: [] });
      return;
    }
    const delim = lines[0].includes("\t") ? "\t" : ",";
    const splitRow = (s) => s.split(delim).map(c => c.trim().replace(/^"(.*)"$/, "$1"));
    const headerRaw = splitRow(lines[0]).map(h => h.toLowerCase().replace(/[\s_-]+/g, ""));
    // Header alias map → canonical key
    const COL_ALIAS = {
      buyer_email: ["buyeremail", "email", "buyer"],
      name:        ["name", "item", "sku", "product", "description"],
      qty:         ["qty", "quantity", "units"],
      price:       ["price", "unitprice", "cost", "rate"],
      terms:       ["terms", "paymentterms"],
      note:        ["note", "notes", "memo"],
    };
    const header = headerRaw.map(h => {
      for (const [canon, aliases] of Object.entries(COL_ALIAS)) {
        if (aliases.includes(h)) return canon;
      }
      return null;
    });
    const required = ["buyer_email", "name", "qty", "price"];
    const missing = required.filter(r => !header.includes(r));
    if (missing.length > 0) {
      setBulkParsed({ rows: [], errors: [`Missing required column(s): ${missing.join(", ")}. Need at minimum: buyer_email, name, qty, price`], groups: [] });
      return;
    }

    const errors = [];
    const validRows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = splitRow(lines[i]);
      const row = {};
      header.forEach((k, idx) => { if (k) row[k] = cells[idx] || ""; });
      const rowNum = i + 1;
      if (!row.buyer_email) { errors.push(`Row ${rowNum}: missing buyer_email`); continue; }
      if (!row.name)        { errors.push(`Row ${rowNum}: missing item name`); continue; }
      const qty = Number(row.qty);
      const price = Number(row.price);
      if (!Number.isFinite(qty) || qty <= 0)    { errors.push(`Row ${rowNum}: invalid qty "${row.qty}"`); continue; }
      if (!Number.isFinite(price) || price < 0) { errors.push(`Row ${rowNum}: invalid price "${row.price}"`); continue; }
      const terms = (row.terms || "net30").toLowerCase();
      if (!["prepaid", "net14", "net30", "net60", "net90"].includes(terms)) {
        errors.push(`Row ${rowNum}: terms must be one of prepaid/net14/net30/net60/net90 (got "${row.terms}")`);
        continue;
      }
      validRows.push({
        buyer_email: row.buyer_email.toLowerCase(),
        name: row.name, qty, price, terms,
        note: row.note || "",
      });
    }

    // Group preview by (buyer_email, terms, note) — same shape the backend uses.
    const groupMap = new Map();
    for (const r of validRows) {
      const key = `${r.buyer_email}||${r.terms}||${r.note}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, { buyer_email: r.buyer_email, terms: r.terms, note: r.note, lines: [], total: 0 });
      }
      const g = groupMap.get(key);
      g.lines.push(r);
      g.total += r.qty * r.price;
    }
    const groups = Array.from(groupMap.values());
    setBulkParsed({ rows: validRows, errors, groups });
  };

  const submitBulk = async () => {
    if (!bulkParsed || bulkParsed.rows.length === 0) return toast.error("Nothing valid to import");
    setSubmittingBulk(true);
    try {
      const { data } = await api.post("/seller/orders/manual/bulk", {
        lines: bulkParsed.rows,
      });
      setBulkResult(data);
      if (data.created_count > 0) {
        toast.success(`${data.created_count} order${data.created_count === 1 ? "" : "s"} created${data.failed_count ? ` · ${data.failed_count} skipped` : ""}`);
        await load();
      } else {
        toast.error("No orders created — see errors below");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setSubmittingBulk(false);
    }
  };

  // How many groups will actually create? Excludes groups whose buyer_email
  // isn't connected to this seller — they'd get rejected by the backend.
  const connectedGroupsCount = useMemo(() => {
    if (!bulkParsed) return 0;
    const known = new Set(connections.map(c => (c.buyer_email || "").toLowerCase()));
    return bulkParsed.groups.filter(g => known.has(g.buyer_email)).length;
  }, [bulkParsed, connections]);

  const downloadBulkTemplate = () => {
    const csv = [
      "buyer_email,name,qty,price,terms,note",
      'buyer@apex.com,"Radiance Serum 30ml · 12-pack",5,42.00,net30,"Phone order from Sarah · booth 42"',
      'buyer@apex.com,"Hydra Mask 50ml · 6-pack",2,18.50,net30,"Phone order from Sarah · booth 42"',
      'someone@retail.com,"Glow Kit",1,99.99,net14,',
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bulk-orders-template.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  // Reset bulk state when the dialog opens or mode changes
  const setOrderModeReset = (mode) => {
    setOrderMode(mode);
    setBulkParsed(null);
    setBulkResult(null);
  };

  // ─── Note on transit/delivery transitions ──────────────────────────────
  // After the buyer's carrier picks up, status flips automatically via
  // carrier webhooks (Warp /api/webhooks/warp + Verqo Shipping /api/webhooks/shippo):
  //   ready_for_pickup → transit → delivered.
  // The seller never needs (and has no way) to manually flip these — they
  // can only monitor, which is exactly what the drawer's Carrier events
  // log provides.

  // CSV export — current filtered view
  const exportCsv = () => {
    if (viewFiltered.length === 0) return toast.error("Nothing to export");
    const headers = ["Order", "Buyer", "Product", "Units", "Total", "Status", "Invoice", "Tracking", "Created"];
    const rows = viewFiltered.map(o => [
      o.code,
      o.buyer_name || "",
      (o.items?.[0]?.name || "").replace(/"/g, '""'),
      o.items?.reduce((s, i) => s + i.qty, 0) || 0,
      o.total || 0,
      STAGE_META[o.stage]?.label || o.stage,
      o.invoice?.code || "",
      o.shipment?.tracking || "",
      o.created_at || "",
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    toast.success(`${viewFiltered.length} rows exported`);
  };

  return (
    <div data-testid="seller-orders">
      {/* Top header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Fulfilment · Orders</div>
          <h1 className="display text-2xl mt-1 tracking-tight">{VIEW_LABEL[view]}</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={openCreateOrder} data-testid="seller-create-order-btn"
            className="rounded-full h-10 px-4 bg-foreground text-background hover:bg-foreground/90">
            <Plus className="w-4 h-4 mr-1.5"/>Create order
          </Button>
          <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn" className="rounded-full h-10 px-4">
            <Download className="w-4 h-4 mr-1.5"/>Export CSV
          </Button>
        </div>
      </div>

      {/* Sticky pill-tab filter bar */}
      <div className="sticky top-[63px] z-10 bg-background/85 backdrop-blur border-b border-border px-4 sm:px-6 lg:px-8 py-3" data-testid="seller-orders-tabbar">
        <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1">
          {VIEW_TABS.map(tab => {
            const ct = counts[tab.countKey] || 0;
            const active = view === tab.key;
            const toneRing = {
              amber:   "ring-amber-200 text-amber-800 bg-amber-50",
              rose:    "ring-rose-200 text-rose-800 bg-rose-50",
              blue:    "ring-blue-200 text-blue-800 bg-blue-50",
              indigo:  "ring-indigo-200 text-indigo-800 bg-indigo-50",
              teal:    "ring-teal-200 text-teal-800 bg-teal-50",
              emerald: "ring-emerald-200 text-emerald-800 bg-emerald-50",
            }[tab.tone] || "ring-foreground/15 bg-secondary";
            return (
              <button
                key={tab.key}
                onClick={() => nav(`/seller/orders#${tab.key}`)}
                data-testid={`seller-tab-${tab.key}`}
                className={`relative shrink-0 inline-flex items-center gap-2 rounded-full text-sm pl-3.5 pr-2 py-1.5 border transition-all ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-white text-foreground/75 border-border hover:border-foreground/30 hover:text-foreground"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5"/>
                <span className="font-medium">{tab.label}</span>
                <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full min-w-[18px] text-center ring-1 ${
                  active
                    ? "bg-background/15 text-background ring-transparent"
                    : ct === 0 ? "text-foreground/40 bg-secondary ring-transparent" : toneRing
                }`}>{ct}</span>
                {tab.action && !active && ct > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-background" title="Needs your action" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-8 space-y-6">

        {view === "claims" && (
          <ClaimsTab role="seller" orders={staged} />
        )}

        {view !== "claims" && <>
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`seller-view-${view}-stats`}>
          {viewStats.map((t, i) => (
            <div key={i} className="rounded-2xl border border-border bg-white p-5">
              <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{t.l}</div>
              <div className={`display ${t.big ? "text-3xl" : "text-2xl"} mt-2 tracking-tight`}>{t.v}</div>
            </div>
          ))}
        </div>

        {/* Composable search + filter (Shopify-style) */}
        <SearchAndFilter
          page="seller_orders"
          search={search}
          onSearchChange={setSearch}
          filters={filters}
          onChange={setFilters}
          totalCount={viewFiltered.length}
          facets={[
            {
              id: "status", label: "Status", icon: Clock, type: "multi",
              options: [
                { value: "confirm",          label: "Awaiting confirmation" },
                { value: "awaiting_pay",     label: "Awaiting payment" },
                { value: "preparing",        label: "Preparing for pickup" },
                { value: "ready_for_pickup", label: "Ready for pickup" },
                { value: "transit",          label: "In transit" },
                { value: "delivered",        label: "Delivered" },
              ],
            },
            {
              id: "buyer", label: "Buyer", icon: User, type: "multi",
              options: buyerOptions,
            },
            { id: "total", label: "Total",      icon: DollarSign,    type: "numeric", numericLabel: "$" },
            { id: "date",  label: "Order date", icon: CalendarDays,  type: "date" },
            {
              id: "terms", label: "Payment terms", icon: Receipt, type: "multi",
              options: [
                { value: "net14", label: "Net-14" },
                { value: "net30", label: "Net-30" },
                { value: "net60", label: "Net-60" },
                { value: "net90", label: "Net-90" },
                { value: "prepaid", label: "Prepay only" },
              ],
            },
            { id: "overdue",     label: "Overdue invoice", icon: AlertTriangle, type: "boolean" },
            { id: "hasTracking", label: "Has tracking",    icon: MapPin,        type: "boolean" },
          ]}
        />

        {/* Bulk-action bar — only renders when at least one row is ticked */}
        {selected.size > 0 && (
          <div
            className="rounded-2xl border-2 border-foreground bg-foreground text-background px-5 py-3 flex flex-wrap items-center gap-3"
            data-testid="seller-bulk-bar"
          >
            <div className="text-sm flex items-center gap-2">
              <span className="display text-base font-semibold" data-testid="seller-bulk-count">
                {selected.size} selected
              </span>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-[11px] underline decoration-background/40 hover:decoration-background"
                data-testid="seller-bulk-clear"
              >
                clear
              </button>
            </div>
            <div className="flex gap-2 ml-auto flex-wrap">
              <Button
                size="sm"
                disabled={bulkBusy || selectedConfirmable.length === 0}
                onClick={bulkConfirm}
                data-testid="seller-bulk-confirm"
                className="rounded-full bg-accent text-foreground hover:bg-accent/90 h-9 px-4 text-xs disabled:opacity-40"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                Confirm {selectedConfirmable.length || ""}
              </Button>
              <Button
                size="sm"
                disabled={bulkBusy || selectedRemindable.length === 0}
                onClick={bulkRemind}
                data-testid="seller-bulk-remind"
                className="rounded-full bg-background/10 text-background hover:bg-background/20 h-9 px-4 text-xs disabled:opacity-40"
              >
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Send reminder {selectedRemindable.length || ""}
              </Button>
              <Button
                size="sm"
                disabled={bulkBusy}
                onClick={bulkPackList}
                data-testid="seller-bulk-pack-list"
                className="rounded-full bg-background/10 text-background hover:bg-background/20 h-9 px-4 text-xs disabled:opacity-40"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Pack list PDF
              </Button>
            </div>
          </div>
        )}

        {/* Table — lean layout (Feb 2026): per-tab column gating, Documents
           cell merges Invoice + Pack list + Tracking, Created → Age in stage,
           View button removed (row click opens drawer), explicit verb on every
           action button + read-only chip for awaiting stages. */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
                <tr>
                  {/* Checkbox: hide on Delivered (no bulk-action makes sense) */}
                  {view !== "delivered" && (
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={el => { if (el) el.indeterminate = someChecked; }}
                        onChange={togglePage}
                        data-testid="seller-bulk-select-all"
                        aria-label="Select all on this page"
                        className="w-4 h-4 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-5 py-3 sticky left-0 bg-secondary/50">Order</th>
                  <th className="px-5 py-3">Buyer</th>
                  <th className="px-5 py-3">Items</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Status</th>
                  {/* Documents column hidden on Action needed (no docs exist
                     pre-confirm and Adjust is inlined next to the action btn).
                     On Delivered, Documents holds Invoice + Pack list while
                     Tracking gets its own dedicated column for click-to-copy. */}
                  {view !== "action" && <th className="px-5 py-3">Documents</th>}
                  {view === "delivered" && <th className="px-5 py-3">Tracking</th>}
                  <th className="px-5 py-3">{view === "delivered" ? "Delivered" : "Age"}</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageData.map(o => {
                  const meta = STAGE_META[o.stage] || STAGE_META.confirm;
                  const totalUnits = o.items?.reduce((s, i) => s + i.qty, 0) || 0;
                  const skuCount  = o.items?.length || 0;
                  const usedSellerTerms = o.invoice?.payment_intent === "seller_terms";
                  const slaTone = ageSlaTone(o, view);
                  const slaBorder = slaTone === "rose"
                    ? "border-l-2 border-l-rose-500"
                    : slaTone === "amber"
                      ? "border-l-2 border-l-amber-400"
                      : "border-l-2 border-l-transparent";
                  return (
                    <tr key={o.id} className={`hover:bg-secondary/30 cursor-pointer ${slaBorder} ${selected.has(o.id) ? "bg-accent/10" : ""}`} data-testid={`seller-row-${o.code}`} onClick={() => openOrder(o.id)}>
                      {view !== "delivered" && (
                        <td className="px-3 py-4 w-10" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(o.id)}
                            onChange={() => toggleOne(o.id)}
                            data-testid={`seller-bulk-row-${o.code}`}
                            aria-label={`Select order ${o.code}`}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-5 py-4 mono text-foreground/70 sticky left-0 bg-white">{o.code}</td>
                      <td className="px-5 py-4">
                        <div className="font-medium flex items-center gap-1.5">
                          {o.buyer_name}
                          {o.buyer_number && (
                            <span
                              data-testid={`order-buyer-number-${o.code}`}
                              title="Customer #"
                              className="text-[10px] mono text-foreground/55 font-normal"
                            >#{o.buyer_number}</span>
                          )}
                        </div>
                        {usedSellerTerms && (
                          <div className="text-[10px] text-emerald-700 mt-0.5 inline-flex items-center gap-1"><Handshake className="w-2.5 h-2.5"/>Free {(o.invoice?.terms || "").toUpperCase()}</div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm whitespace-nowrap">
                          {skuCount} SKU{skuCount === 1 ? "" : "s"}
                          <span className="text-foreground/40 mx-1.5">·</span>
                          <span className="text-foreground/65">{totalUnits} units</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 display">{money(o.total)}</td>
                      <td className="px-5 py-4"><span className={`chip ${meta.chip}`}>{meta.emoji} {meta.label}</span></td>
                      {view !== "action" && (
                        <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                          <DocumentsCell order={o} omitTracking={view === "delivered"} />
                        </td>
                      )}
                      {view === "delivered" && (
                        <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                          {o.shipment?.tracking
                            ? <TrackingCell shipment={o.shipment}/>
                            : <span className="text-foreground/30 text-xs">—</span>}
                        </td>
                      )}
                      <td className="px-5 py-4 text-xs text-foreground/60" data-testid={`seller-age-${o.code}`}>
                        {view === "delivered"
                          ? (o.shipment?.delivered_at
                              ? new Date(o.shipment.delivered_at).toLocaleDateString()
                              : new Date(o.created_at).toLocaleDateString())
                          : (
                            <span className={
                              slaTone === "rose"  ? "mono text-rose-700 font-medium" :
                              slaTone === "amber" ? "mono text-amber-700 font-medium" :
                              "mono"
                            }>
                              {ageInStageLabel(o)}
                              {slaTone && <span className="ml-1 text-[9px] uppercase tracking-widest">SLA</span>}
                            </span>
                          )}
                      </td>
                      <td className="px-5 py-4 text-right space-x-1" onClick={e => e.stopPropagation()}>
                        {o.stage === "confirm" && (
                          <Button size="sm" onClick={() => openConfirm(o)} data-testid={`seller-confirm-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">Confirm order</Button>
                        )}
                        {o.stage === "preparing" && (
                          <span className="inline-flex items-center gap-1.5">
                            <Button size="sm" onClick={() => openShippingInfo(o)} data-testid={`seller-ship-info-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">Enter shipping info</Button>
                            <AdjustOrderCell order={o} onClick={inv => setAdjustTarget(inv)} />
                          </span>
                        )}
                        {o.stage === "awaiting_pay" && (
                          <Button size="sm" variant="outline" onClick={() => sendOneReminder(o.id)} data-testid={`seller-remind-${o.code}`} className="rounded-full">Send reminder</Button>
                        )}
                        {o.stage === "ready_for_pickup" && (
                          o.shipping_mode === "buyer_arranged" ? (
                            <Button size="sm" onClick={() => openRelease(o)} data-testid={`seller-release-${o.code}`} className="rounded-full bg-foreground text-background hover:bg-foreground/90">Release to buyer</Button>
                          ) : (
                            <span className="text-[10px] mono italic text-foreground/50 inline-flex items-center gap-1" data-testid={`seller-await-pickup-${o.code}`}>
                              <Truck className="w-3 h-3"/>Awaiting carrier
                            </span>
                          )
                        )}
                        {o.stage === "transit" && (
                          <span className="text-[10px] mono italic text-foreground/50 inline-flex items-center gap-1" data-testid={`seller-await-delivery-${o.code}`}>
                            <Truck className="w-3 h-3"/>In transit
                          </span>
                        )}
                        {o.stage === "delivered" && (
                          <span className="text-[10px] mono italic text-foreground/50 inline-flex items-center gap-1" data-testid={`seller-delivered-${o.code}`}>
                            <Check className="w-3 h-3"/>Complete
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {pageData.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-0" data-testid="seller-orders-empty">
                      {/* Sample preview — Phase 2 of the empty-state pattern.
                       *  Renders ONLY after the first fetch resolves AND the
                       *  seller has TRULY zero real orders. This prevents the
                       *  3-sample flash on accounts (like the demo seed) that
                       *  load real orders ~3s later. */}
                      {!initialLoaded
                        ? (
                          <div className="py-16 text-center text-foreground/40 text-xs" data-testid="seller-orders-loading">
                            <div className="inline-block w-5 h-5 rounded-full border-2 border-foreground/15 border-t-foreground/50 animate-spin" />
                            <div className="mt-3">Loading your orders…</div>
                          </div>
                        )
                        : orders.length === 0 && !search && view === "all"
                        ? <SampleOrdersPreview onClose={() => {}} />
                        : (
                          <div className="text-center py-16 text-foreground/55 text-sm">
                            <div className="display text-base">Nothing here yet</div>
                            <div className="text-xs mt-1 text-foreground/50 max-w-md mx-auto">
                              {view === "action"    && "All caught up — no orders waiting on you to confirm or pack right now."}
                              {view === "progress"  && "Nothing in flight right now. Orders awaiting buyer payment, ready for carrier pickup, or in transit will all appear here."}
                              {view === "delivered" && "Your completed orders will appear here once the carrier reports delivery."}
                              {view === "all"       && "Once buyers start placing orders, they'll appear here."}
                            </div>
                          </div>
                        )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs">
              <span className="text-foreground/60">Page {page} of {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1 rounded-md border border-border disabled:opacity-30 hover:bg-secondary">Back</button>
                {Array.from({length: totalPages}, (_, i) => (
                  <button key={i} onClick={()=>setPage(i+1)} className={`px-3 py-1 rounded-md ${page===i+1?"bg-foreground text-background":"hover:bg-secondary"}`}>{i+1}</button>
                ))}
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="px-3 py-1 rounded-md border border-border disabled:opacity-30 hover:bg-secondary">Next</button>
              </div>
            </div>
          )}
        </div>
        </>}
      </div>

      {/* Order detail drawer */}
      <Sheet open={!!drawer} onOpenChange={(v)=>!v && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="seller-order-drawer">
          <SheetTitle className="sr-only">Order detail</SheetTitle>
          {drawer && (() => {
            const o = drawer.order;
            const inv = drawer.invoice;
            const sh = drawer.shipment;
            const stage = getSellerStage(o, inv);
            const meta = STAGE_META[stage] || STAGE_META.confirm;
            return <>
              <SheetHeader>
                <div className="display text-xl flex items-center justify-between gap-2" data-testid="drawer-order-code">
                  <span>Order {o.code}</span>
                  <span className={`chip ${meta.chip}`}>{meta.emoji} {meta.label}</span>
                </div>
              </SheetHeader>

              {/* Top summary + quick actions */}
              <div className="mt-5 rounded-xl bg-secondary/50 p-4 flex justify-between items-end">
                <div>
                  <div className="text-xs uppercase tracking-widest text-foreground/60">Order total</div>
                  <div className="display text-3xl mt-1">{money(o.total)}</div>
                  <div className="text-xs text-foreground/60 mt-1">{(o.terms || "").toUpperCase()} · {o.buyer_name}</div>
                </div>
                <div className="flex flex-col gap-1.5">
                  {stage === "confirm" && (
                    <Button size="sm" onClick={() => { openConfirm(o); }} data-testid="drawer-confirm" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Confirm & invoice
                    </Button>
                  )}
                  {stage === "confirm" && offersPrebook && (
                    <Button size="sm" variant="outline" onClick={() => openPrebook(o)} data-testid="drawer-convert-prebook" className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-50">
                      <Clock className="w-3.5 h-3.5 mr-1"/>Convert to prebook
                    </Button>
                  )}
                  {/* Active prebook — seller can request an extension; only
                      meaningful once we're past the promised date but we
                      surface it any time during prebook_active so sellers
                      can proactively flag delays. */}
                  {o.status === "prebook_active" && (
                    <Button size="sm" variant="outline" onClick={() => openExtension(o)} data-testid="drawer-prebook-request-extension" className="rounded-full">
                      <RotateCcw className="w-3.5 h-3.5 mr-1"/>Request extension
                    </Button>
                  )}
                  {stage === "preparing" && (
                    <Button size="sm" onClick={() => openShippingInfo(o)} data-testid="drawer-ship-info" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                      <Package className="w-3.5 h-3.5 mr-1"/>Enter shipping info
                    </Button>
                  )}
                  {stage === "ready_for_pickup" && (
                    <div className="text-[11px] text-foreground/55 max-w-[180px] text-right" data-testid="drawer-await-pickup">
                      Awaiting carrier pickup. Status updates automatically when the carrier scans.
                    </div>
                  )}
                  {stage === "transit" && (
                    <div className="text-[11px] text-foreground/55 max-w-[180px] text-right" data-testid="drawer-await-delivery">
                      In transit. Status updates automatically when the carrier marks delivered.
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadOrderPo(o)}
                    data-testid="drawer-download-po"
                    className="rounded-full"
                  >
                    <Download className="w-3.5 h-3.5 mr-1"/>PO
                  </Button>
                  {inv && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadOrderInvoice(inv)}
                      data-testid="drawer-download-invoice"
                      className="rounded-full"
                    >
                      <Download className="w-3.5 h-3.5 mr-1"/>Invoice
                    </Button>
                  )}
                </div>
              </div>

              {/* Timeline */}
              {drawer.timeline && drawer.timeline.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Fulfilment timeline</div>
                  <div className="space-y-3">
                    {drawer.timeline.map((t, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        {t.done ? <CheckCircle2 className="w-5 h-5 text-foreground mt-0.5"/> : <Circle className="w-5 h-5 text-foreground/30 mt-0.5"/>}
                        <div className="flex-1">
                          <div className="text-sm font-medium">{t.label}</div>
                          <div className="text-xs text-foreground/50">{new Date(t.date).toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Items */}
              <div className="mt-5">
                <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Order items</div>
                <div className="rounded-xl border border-border divide-y divide-border">
                  {(o.items || []).map((it, i) => (
                    <div key={i} className="flex justify-between p-3 text-sm">
                      <div>
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-foreground/60">{it.qty} units × {money(it.price)}</div>
                      </div>
                      <div className="display">{money(it.qty * it.price)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cancellation receipt — visible when the order is cancelled
                  or auto-cancelled. Surfaces the restocking-fee breakdown
                  + a one-click waive/adjust control for the seller. */}
              {(o.status === "cancelled" || o.status === "auto_cancelled") && o.cancellation && (
                <div className="mt-5" data-testid="seller-cancellation-card">
                  <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Cancellation & restocking</div>
                  <div className="rounded-xl border border-border p-4 space-y-2 bg-white">
                    <div className="flex justify-between text-[13px]"><span className="text-foreground/60">Cancelled by</span><span>{o.cancellation.by_name || o.cancellation.by_user_id} · {o.cancellation.type === "pickup_timeout_auto_cancel" ? "30-day pickup timeout" : o.cancellation.type === "buyer_cancel" ? "Buyer cancel" : (o.cancellation.type || "—")}</span></div>
                    <div className="flex justify-between text-[13px]"><span className="text-foreground/60">Cancelled at</span><span>{new Date(o.cancellation.cancelled_at || o.cancelled_at).toLocaleString()}</span></div>
                    {o.cancellation.reason && (
                      <div className="flex justify-between text-[13px] gap-3"><span className="text-foreground/60 shrink-0">Reason</span><span className="text-right">{o.cancellation.reason}</span></div>
                    )}
                    <div className="border-t border-border my-2"/>
                    <div className="flex justify-between text-[13px]"><span className="text-foreground/60">Order total</span><span className="mono">{money(o.total)}</span></div>
                    <div className="flex justify-between text-[13px]"><span className="text-foreground/60">Restocking fee ({(o.cancellation.restocking_pct || 0).toFixed(1)}%)</span><span className="mono text-red-600">−{money(o.cancellation.restocking_fee || 0)}</span></div>
                    <div className="flex justify-between text-[14px] font-medium"><span>Refund to buyer</span><span className="mono">{money(o.cancellation.refund_amount || 0)}</span></div>
                    {o.cancellation.fee_waived && (
                      <div className="text-[11px] text-emerald-700 mono uppercase tracking-widest">Fee waived{o.cancellation.adjust_reason ? ` · ${o.cancellation.adjust_reason}` : ""}</div>
                    )}
                    <div className="pt-2 flex flex-wrap gap-2">
                      {!o.cancellation.fee_waived && (o.cancellation.restocking_fee || 0) > 0 && (
                        <Button
                          size="sm" variant="outline"
                          data-testid="seller-waive-restocking-btn"
                          onClick={async () => {
                            const reason = window.prompt("Waive the restocking fee on this order? Optional reason (e.g., 'long-time VIP'):", "");
                            if (reason === null) return;
                            try {
                              await api.patch(`/orders/${o.id}/restocking-fee`, { restocking_fee: 0, reason });
                              toast.success("Restocking fee waived. Buyer was notified.");
                              const { data } = await api.get(`/orders/${o.id}`);
                              setDrawer(data);
                              load();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Could not waive fee.");
                            }
                          }}
                          className="rounded-full"
                        >Waive restocking fee</Button>
                      )}
                      <Button
                        size="sm" variant="ghost"
                        data-testid="seller-adjust-restocking-btn"
                        onClick={async () => {
                          const max = (o.total * (o.cancellation.restocking_pct || 0) / 100);
                          const raw = window.prompt(`Set the restocking fee for this order. Allowed range: $0.00 – $${max.toFixed(2)} (policy cap).`, String(o.cancellation.restocking_fee || 0));
                          if (raw === null) return;
                          const n = Number(raw);
                          if (!Number.isFinite(n) || n < 0) { toast.error("Invalid amount"); return; }
                          const reason = window.prompt("Reason for the adjustment (optional):", "");
                          if (reason === null) return;
                          try {
                            await api.patch(`/orders/${o.id}/restocking-fee`, { restocking_fee: n, reason });
                            toast.success("Restocking fee updated. Buyer was notified.");
                            const { data } = await api.get(`/orders/${o.id}`);
                            setDrawer(data);
                            load();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Could not adjust fee.");
                          }
                        }}
                        className="rounded-full"
                      >Adjust fee</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping (seller view) — info you owe the buyer + live monitor.
                  Combines: (a) the package & pickup info the seller commits to,
                  (b) the carrier's tracking record + raw webhook events. */}
              {(o.expected_ready_at || o.shipping_info || sh) && (() => {
                const info = o.shipping_info || {};
                const isLate = o.expected_ready_at && !o.ready_to_ship &&
                  new Date(o.expected_ready_at).getTime() < Date.now();
                const events = drawer.shipment_events || [];
                return (
                  <div className="mt-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs uppercase tracking-widest text-foreground/60">Shipping</div>
                      {(stage === "preparing" || stage === "ready_for_pickup") && (
                        <button
                          onClick={() => o.shipping_info ? openShippingInfo(o) : openSetReadyDate(o)}
                          data-testid="drawer-edit-shipping"
                          className="text-[11px] inline-flex items-center gap-1 text-foreground/60 hover:text-foreground"
                        >
                          <Pencil className="w-3 h-3"/> {o.shipping_info ? "Edit shipping info" : "Edit ready date"}
                        </button>
                      )}
                    </div>

                    <div className="rounded-xl border border-border p-4 space-y-1.5 text-sm">
                      {o.expected_ready_at && (
                        <div className="flex justify-between items-center">
                          <span className="text-foreground/60">Ready by</span>
                          <span className={`font-medium ${isLate ? "text-rose-700" : ""}`}>
                            {new Date(o.expected_ready_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                            {isLate && <span className="ml-2 text-[10px] rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5"><AlertTriangle className="w-3 h-3 inline -mt-0.5"/> Past due</span>}
                          </span>
                        </div>
                      )}
                      {info.package_count != null && (
                        <div className="flex justify-between"><span className="text-foreground/60">Packages</span><span>{info.package_count} pkg{info.package_count === 1 ? "" : "s"} · {info.total_weight_lb} lb</span></div>
                      )}
                      {info.package_dimensions && (
                        <div className="flex justify-between"><span className="text-foreground/60">Dimensions</span><span className="mono text-xs">{info.package_dimensions}</span></div>
                      )}
                      {info.pickup_location && (
                        <div className="flex justify-between gap-3"><span className="text-foreground/60 shrink-0">Pickup</span><span className="text-xs text-right">{info.pickup_location}</span></div>
                      )}
                      {info.pickup_window && (
                        <div className="flex justify-between"><span className="text-foreground/60">Window</span><span className="text-xs">{info.pickup_window}</span></div>
                      )}
                      {info.bol_number && (
                        <div className="flex justify-between"><span className="text-foreground/60">BOL #</span><span className="mono text-xs">{info.bol_number}</span></div>
                      )}
                      {info.pallet_count != null && info.pallet_count !== "" && Number(info.pallet_count) > 0 && (
                        <div className="flex justify-between"><span className="text-foreground/60">Pallets</span><span>{info.pallet_count} pallet{Number(info.pallet_count) === 1 ? "" : "s"}</span></div>
                      )}
                      {info.pickup_notes && (
                        <div className="text-xs text-foreground/60 pt-2 border-t border-border/60 mt-2">{info.pickup_notes}</div>
                      )}

                      {sh && (
                        <>
                          <div className="border-t border-border/60 my-2"/>
                          <div className="flex justify-between"><span className="text-foreground/60">Carrier</span><span>{sh.carrier}</span></div>
                          <div className="flex justify-between"><span className="text-foreground/60">Tracking #</span><span className="mono text-xs">{sh.tracking}</span></div>
                          {sh.origin && sh.destination && (
                            <div className="flex justify-between gap-3"><span className="text-foreground/60 shrink-0">Route</span><span className="text-xs text-right">{sh.origin} → {sh.destination}</span></div>
                          )}
                          <div className="flex justify-between"><span className="text-foreground/60">Status</span><span className="chip bg-secondary">{sh.status}</span></div>
                        </>
                      )}
                    </div>

                    {/* Carrier event log (Warp / Verqo Shipping webhooks) */}
                    {events.length > 0 && (
                      <div className="mt-3 rounded-xl border border-border bg-secondary/20 p-4">
                        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-2.5">Carrier events</div>
                        <div className="space-y-2">
                          {events.slice(0, 6).map((ev, i) => (
                            <div key={i} className="flex gap-2 items-start text-xs">
                              <Truck className="w-3.5 h-3.5 text-foreground/40 mt-0.5 shrink-0"/>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{(ev.status || ev.event_type || "update").toString().replace(/_/g, " ")}</div>
                                <div className="text-foreground/50 text-[11px]">{new Date(ev.occurred_at || ev.received_at).toLocaleString()}</div>
                              </div>
                            </div>
                          ))}
                          {events.length > 6 && (
                            <div className="text-[10px] text-foreground/45 pt-1">+ {events.length - 6} earlier events</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Invoice & terms */}
              {inv && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-widest text-foreground/60 mb-3">Invoice &amp; terms</div>
                  <div className="rounded-xl border border-border p-4 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-foreground/60">Invoice</span><span className="mono">{inv.code}</span></div>
                    <div className="flex justify-between"><span className="text-foreground/60">Terms</span><span>{(inv.terms || "").toUpperCase()}</span></div>
                    <div className="flex justify-between"><span className="text-foreground/60">Amount</span><span className="display">{money(inv.amount)}</span></div>
                    <div className="flex justify-between"><span className="text-foreground/60">Status</span>
                      <span className={`chip ${inv.status === "paid" ? "bg-accent/30 border border-accent/40" : "bg-secondary"}`}>{inv.status}</span>
                    </div>
                    {inv.payment_intent === "seller_terms" && (
                      <div className="flex justify-between"><span className="text-foreground/60">Via</span>
                        <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]"><Handshake className="w-3 h-3"/>Your free terms</span>
                      </div>
                    )}
                    {inv.extension_terms && (
                      <div className="flex justify-between"><span className="text-foreground/60">Extended to</span>
                        <span className="chip bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px]"><Sparkles className="w-3 h-3"/>{inv.extension_terms.toUpperCase()}</span>
                      </div>
                    )}
                    {/* Phase IV — invoice validity countdown surfaced in
                     *  the seller's order drawer too. Lets the seller see
                     *  at a glance which buyer-side invoices are about to
                     *  void (and may need a friendly nudge or a re-issue). */}
                    {inv.expires_at && (
                      <div className="flex justify-between items-center pt-1.5 border-t border-border/60">
                        <span className="text-foreground/60">Validity</span>
                        <span className="inline-flex items-center gap-1.5">
                          <InvoiceExpiryChip invoice={inv} compact/>
                          <span className="text-[10.5px] text-foreground/55">
                            auto-voids on {new Date(inv.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        </span>
                      </div>
                    )}
                    {inv.voided_reason === "validity_expired" && (
                      <div className="mt-2 rounded-lg border border-rose-300 bg-rose-50/60 p-2 text-[11.5px] text-rose-900 flex items-start gap-2" data-testid={`seller-drawer-auto-voided-${inv.code}`}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                        <div>
                          <div className="font-medium mb-0.5">Auto-voided — buyer didn't accept in time</div>
                          <div className="leading-snug">Validity window elapsed. You can re-issue at the current price by creating a new order with this buyer.</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </>;
          })()}
        </SheetContent>
      </Sheet>

      {/* Confirm dialog — issues the invoice AND commits the ready date in one step. */}
      <Dialog open={!!confirming} onOpenChange={(v) => !v && setConfirming(null)}>
        <DialogContent className="max-w-md" data-testid="seller-confirm-dialog">
          {confirming && (
            <>
              <DialogHeader><DialogTitle className="display text-2xl tracking-tight">Confirm order {confirming.code}</DialogTitle></DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">
                Confirms the order, issues the invoice, and commits the ready-by date the buyer will see — all in one step.
              </p>
              <div className="mt-4 rounded-xl bg-secondary/30 border border-border p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-foreground/55">Buyer</span><span className="font-medium">{confirming.buyer_name}</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Items</span><span>{confirming.items?.length} SKU · {confirming.items?.reduce((s,i)=>s+i.qty,0)} units</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Total</span><span className="display">{money(confirming.total)}</span></div>
                <div className="flex justify-between"><span className="text-foreground/55">Terms</span><span className="uppercase">{confirming.terms}</span></div>
              </div>

              <div className="mt-4">
                <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">Ready by</label>
                <Input type="date"
                  value={confirmReadyDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={e => setConfirmReadyDate(e.target.value)}
                  className="h-11 display"
                  data-testid="seller-confirm-ready-date"/>
                {/* Smart hint: only render when default lead time is configured
                    AND we're using it (i.e. order had no prior date) */}
                {!confirming.expected_ready_at && typeof defaultLeadDays === "number" && (
                  <div className="mt-1.5 text-[11px] text-foreground/55 inline-flex items-center gap-1" data-testid="confirm-ready-prefill-hint">
                    <Sparkles className="w-3 h-3"/>
                    Pre-filled from your default lead time ({defaultLeadDays} day{defaultLeadDays === 1 ? "" : "s"}). <a href="/seller/settings#operations" className="underline hover:no-underline">Change</a>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[2, 5, 7, 14].map(d => (
                    <button key={d} type="button"
                      onClick={() => setConfirmReadyDate(new Date(Date.now() + d * 86400000).toISOString().slice(0, 10))}
                      className="text-[11px] rounded-full border border-border px-3 py-1 hover:border-foreground/40">
                      In {d} days
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={submitConfirm} data-testid="seller-submit-confirm"
                className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                Confirm &amp; issue invoice
              </Button>
              <p className="text-[10px] text-center text-foreground/45 mt-2">Buyer notified instantly · invoice generated · ready-by date committed</p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Set ready date dialog (post-payment) */}
      <Dialog open={!!readyDateOrder} onOpenChange={(v) => { if (!v) { setReadyDateOrder(null); setReadyDateValue(""); } }}>
        <DialogContent className="max-w-md" data-testid="seller-ready-date-dialog">
          {readyDateOrder && (
            <>
              <DialogHeader><DialogTitle className="display text-2xl tracking-tight">Set ready date · {readyDateOrder.code}</DialogTitle></DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">
                Tell {readyDateOrder.buyer_name} when the order will be packed and ready for their carrier to pick up.
              </p>
              <div className="mt-4">
                <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-2 block">Ready by</label>
                <Input type="date" value={readyDateValue} onChange={e => setReadyDateValue(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="h-12 text-lg display" data-testid="seller-ready-date-input"/>
                {/* When the seller has configured a default lead time and we're
                    NOT editing an existing date, show a quiet "pre-filled from
                    settings" hint so the date doesn't feel arbitrary. */}
                {!readyDateOrder.expected_ready_at && typeof defaultLeadDays === "number" && (
                  <div className="mt-1.5 text-[11px] text-foreground/55 inline-flex items-center gap-1" data-testid="ready-date-prefill-hint">
                    <Sparkles className="w-3 h-3"/>
                    Pre-filled from your default lead time ({defaultLeadDays} day{defaultLeadDays === 1 ? "" : "s"}). <a href="/seller/settings#operations" className="underline hover:no-underline">Change</a>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[2, 5, 7, 14].map(d => (
                    <button key={d} onClick={() => setReadyDateValue(new Date(Date.now() + d * 86400000).toISOString().slice(0, 10))}
                      className="text-[11px] rounded-full border border-border px-3 py-1 hover:border-foreground/40">
                      In {d} days
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={submitReadyDate} data-testid="seller-submit-ready-date"
                className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                Save &amp; notify buyer
              </Button>
              <p className="text-[10px] text-center text-foreground/45 mt-2">Buyer is notified instantly. You can update this date anytime before packing.</p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Enter shipping info dialog (when goods are physically packed) */}
      <Dialog open={!!shipInfoOrder} onOpenChange={(v) => !v && setShipInfoOrder(null)}>
        <DialogContent className="max-w-lg" data-testid="seller-shipping-info-dialog">
          {shipInfoOrder && (
            <>
              <DialogHeader><DialogTitle className="display text-2xl tracking-tight">Shipping info · {shipInfoOrder.code}</DialogTitle></DialogHeader>
              <p className="text-sm text-foreground/55 -mt-1">
                Goods are packed. Share the package & pickup details so {shipInfoOrder.buyer_name} can book a carrier.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                {/* Pallet count first — drives whether this ships parcel or LTL */}
                <div className="col-span-2">
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">
                    Pallet count <span className="text-foreground/40 normal-case">(0 if parcel · LTL otherwise)</span>
                  </label>
                  <Input type="number" min={0} value={shipInfo.pallet_count}
                    onChange={e => setShipInfo({ ...shipInfo, pallet_count: e.target.value })}
                    placeholder='0'
                    data-testid="ship-info-pallets"/>
                </div>
                <div>
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">Packages</label>
                  <Input type="number" min={1} value={shipInfo.package_count}
                    onChange={e => setShipInfo({ ...shipInfo, package_count: e.target.value })}
                    data-testid="ship-info-pkg-count"/>
                </div>
                <div>
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">Total weight (lb)</label>
                  <Input type="number" min={0.1} step="0.1" value={shipInfo.total_weight_lb}
                    onChange={e => setShipInfo({ ...shipInfo, total_weight_lb: e.target.value })}
                    placeholder="120"
                    data-testid="ship-info-weight"/>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">Dimensions <span className="text-foreground/40 normal-case">(optional)</span></label>
                  <Input value={shipInfo.package_dimensions}
                    onChange={e => setShipInfo({ ...shipInfo, package_dimensions: e.target.value })}
                    placeholder='48 x 40 x 36 in'
                    data-testid="ship-info-dims"/>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">Pickup location <span className="text-rose-500">*</span> <span className="text-foreground/40 normal-case">(defaults to your warehouse from Settings)</span></label>
                  <Input value={shipInfo.pickup_location}
                    onChange={e => setShipInfo({ ...shipInfo, pickup_location: e.target.value })}
                    placeholder='215 Clayton St., Dock B, San Francisco CA 94117'
                    data-testid="ship-info-pickup"/>
                </div>
                <div>
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">Pickup window <span className="text-foreground/40 normal-case">(default from Settings)</span></label>
                  <Input value={shipInfo.pickup_window}
                    onChange={e => setShipInfo({ ...shipInfo, pickup_window: e.target.value })}
                    placeholder='Mon–Fri 9am–5pm'
                    data-testid="ship-info-window"/>
                </div>
                <div>
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">BOL # <span className="text-foreground/40 normal-case">(optional)</span></label>
                  <Input value={shipInfo.bol_number}
                    onChange={e => setShipInfo({ ...shipInfo, bol_number: e.target.value })}
                    placeholder='BOL-00123'
                    data-testid="ship-info-bol"/>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1 block">Pickup notes</label>
                  <Input value={shipInfo.pickup_notes}
                    onChange={e => setShipInfo({ ...shipInfo, pickup_notes: e.target.value })}
                    placeholder='Forklift required · ring buzzer at dock B'
                    data-testid="ship-info-notes"/>
                </div>
              </div>
              <Button onClick={submitShippingInfo} data-testid="seller-submit-shipping-info"
                className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
                Mark ready &amp; notify buyer
              </Button>
              <p className="text-[10px] text-center text-foreground/45 mt-2">Order moves to <span className="font-medium">Ready for pickup</span>. Buyer can now schedule their carrier.</p>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Release-to-buyer dialog — buyer-arranged shipping handoff capture */}
      <Dialog open={!!releaseOrder} onOpenChange={(v) => !v && setReleaseOrder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="seller-release-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Release order to buyer</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/60 -mt-1">
            The buyer chose to arrange their own shipping. Capture who's picking up the goods, optional photo proof, and an acknowledgment — we'll generate a signed release receipt and close the order.
          </p>
          {releaseOrder && (
            <>
              <div className="rounded-lg bg-secondary/40 p-3 mt-3 text-[12.5px] text-foreground/70 space-y-0.5">
                <div className="flex justify-between"><span>Order</span><span className="mono font-medium">{releaseOrder.code}</span></div>
                <div className="flex justify-between"><span>Buyer</span><span>{releaseOrder.buyer_name}</span></div>
                <div className="flex justify-between"><span>Items</span><span>{(releaseOrder.items || []).reduce((s, i) => s + (i.qty || 0), 0)} units · {(releaseOrder.items || []).length} sku{(releaseOrder.items || []).length === 1 ? "" : "s"}</span></div>
              </div>

              <div className="space-y-3 mt-3">
                <div>
                  <label className="text-[12px] mono uppercase tracking-widest text-foreground/55">Recipient name *</label>
                  <input
                    type="text"
                    value={releaseForm.recipient_name}
                    onChange={(e) => setReleaseForm(f => ({ ...f, recipient_name: e.target.value }))}
                    placeholder="e.g. Carlos Rivera"
                    data-testid="release-recipient-name"
                    className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-foreground/30"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[12px] mono uppercase tracking-widest text-foreground/55">Company (optional)</label>
                    <input
                      type="text"
                      value={releaseForm.recipient_company}
                      onChange={(e) => setReleaseForm(f => ({ ...f, recipient_company: e.target.value }))}
                      placeholder="e.g. XPO Logistics"
                      data-testid="release-recipient-company"
                      className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-[13.5px]"
                    />
                  </div>
                  <div>
                    <label className="text-[12px] mono uppercase tracking-widest text-foreground/55">Phone (optional)</label>
                    <input
                      type="tel"
                      value={releaseForm.recipient_phone}
                      onChange={(e) => setReleaseForm(f => ({ ...f, recipient_phone: e.target.value }))}
                      placeholder="+1 555-0142"
                      data-testid="release-recipient-phone"
                      className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-[13.5px]"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[12px] mono uppercase tracking-widest text-foreground/55">Photo proof (up to 4)</label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {releaseForm.photo_paths.map((p, i) => (
                      <div key={p} className="relative w-16 h-16 rounded-md border border-border overflow-hidden bg-secondary" data-testid={`release-photo-${i}`}>
                        <div className="absolute inset-0 grid place-items-center text-[10px] mono text-foreground/55">PHOTO #{i + 1}</div>
                        <button
                          type="button"
                          onClick={() => setReleaseForm(f => ({ ...f, photo_paths: f.photo_paths.filter(x => x !== p) }))}
                          aria-label="Remove"
                          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-foreground/80 text-background text-[10px] grid place-items-center"
                          data-testid={`release-photo-remove-${i}`}
                        >×</button>
                      </div>
                    ))}
                    {releaseForm.photo_paths.length < 4 && (
                      <label className="w-16 h-16 rounded-md border border-dashed border-border grid place-items-center cursor-pointer hover:border-foreground/40">
                        <span className="text-[10px] mono text-foreground/55">+ Photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => uploadReleasePhoto(e.target.files?.[0])}
                          data-testid="release-photo-input"
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-[11px] text-foreground/50 mt-1">Optional but strongly recommended — pickup vehicle, signed BOL, or palletized goods.</p>
                </div>
                <div>
                  <label className="text-[12px] mono uppercase tracking-widest text-foreground/55">Acknowledgment (recipient typed name)</label>
                  <input
                    type="text"
                    value={releaseForm.signature_text}
                    onChange={(e) => setReleaseForm(f => ({ ...f, signature_text: e.target.value }))}
                    placeholder='e.g. "Carlos Rivera"'
                    data-testid="release-signature-text"
                    className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-[13.5px] mono"
                  />
                  <p className="text-[11px] text-foreground/50 mt-1">Ask the recipient to type their name. Counts as e-signature on the receipt.</p>
                </div>
                <div>
                  <label className="text-[12px] mono uppercase tracking-widest text-foreground/55">Notes (optional)</label>
                  <textarea
                    rows={2}
                    value={releaseForm.notes}
                    onChange={(e) => setReleaseForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Anything worth noting — pallets sealed, photos taken on dock 3, etc."
                    data-testid="release-notes"
                    className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-[13.5px]"
                  />
                </div>

                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[11.5px] leading-snug text-amber-900">
                  <strong>Heads up:</strong> Once released, this order moves to <em>Delivered</em>. Transit-damage and lost-in-transit claims are disabled for buyer-arranged shipments — the release receipt is your proof on file.
                </div>

                <Button
                  onClick={submitRelease}
                  disabled={releaseSubmitting || !releaseForm.recipient_name.trim()}
                  data-testid="release-submit"
                  className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40 mt-1"
                >
                  {releaseSubmitting ? "Releasing…" : "Release order & generate receipt"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Create-order dialog — manual entry for phone / trade-show / rep orders */}
      <Dialog open={creating} onOpenChange={(v) => !v && setCreating(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="seller-create-order-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Create order on a buyer's account</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground/55 -mt-1">
            For phone, email, trade-show, or sales-rep orders. The buyer is notified instantly and the invoice is issued straight away.
          </p>

          {/* Mode toggle */}
          <div className="mt-4 inline-flex rounded-full bg-secondary p-1 self-start" data-testid="new-order-mode-toggle">
            <button
              type="button"
              onClick={() => setOrderModeReset("single")}
              data-testid="new-order-mode-single"
              className={`text-xs px-4 py-1.5 rounded-full transition ${orderMode === "single" ? "bg-white shadow-sm text-foreground" : "text-foreground/60 hover:text-foreground"}`}
            >
              Single order
            </button>
            <button
              type="button"
              onClick={() => setOrderModeReset("bulk")}
              data-testid="new-order-mode-bulk"
              className={`text-xs px-4 py-1.5 rounded-full transition ${orderMode === "bulk" ? "bg-white shadow-sm text-foreground" : "text-foreground/60 hover:text-foreground"}`}
            >
              Paste from spreadsheet
            </button>
          </div>

          {orderMode === "single" && (
          <div className="mt-4 space-y-4" data-testid="new-order-single-pane">
            {/* Buyer + terms */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">Buyer</label>
                <Select value={newOrder.buyer_id} onValueChange={(v) => setNewOrder(o => ({ ...o, buyer_id: v }))}>
                  <SelectTrigger data-testid="new-order-buyer" className="h-11">
                    <SelectValue placeholder={connections.length === 0 ? "No connected buyers yet" : "Select a buyer"}/>
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map(c => (
                      <SelectItem key={c.buyer_id} value={c.buyer_id}>
                        {c.buyer_name || c.buyer_email || c.buyer_id}
                        {c.buyer_company ? ` · ${c.buyer_company}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">Payment terms</label>
                <Select value={newOrder.terms} onValueChange={(v) => setNewOrder(o => ({ ...o, terms: v }))}>
                  <SelectTrigger data-testid="new-order-terms" className="h-11">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prepaid">Prepay</SelectItem>
                    <SelectItem value="net14">Net-14</SelectItem>
                    <SelectItem value="net30">Net-30</SelectItem>
                    <SelectItem value="net60">Net-60</SelectItem>
                    <SelectItem value="net90">Net-90</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Line items</label>
                <button type="button" onClick={addLine} data-testid="new-order-add-line"
                  className="text-[11px] inline-flex items-center gap-1 text-foreground/60 hover:text-foreground">
                  <Plus className="w-3 h-3"/> Add line
                </button>
              </div>
              <div className="rounded-xl border border-border divide-y divide-border">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 bg-secondary/30">
                  <div className="col-span-6">Item / SKU</div>
                  <div className="col-span-2 text-center">Qty</div>
                  <div className="col-span-3 text-right">Unit price</div>
                  <div className="col-span-1"/>
                </div>
                {newOrder.lines.map((line, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`new-order-line-${i}`}>
                    <Input
                      className="col-span-6 h-9"
                      placeholder='e.g. "Radiance Serum 30ml · 12-pack"'
                      value={line.name}
                      onChange={e => updateLine(i, { name: e.target.value })}
                      data-testid={`new-order-line-name-${i}`}
                    />
                    <Input
                      type="number" min="1"
                      className="col-span-2 h-9 text-center"
                      value={line.qty}
                      onChange={e => updateLine(i, { qty: e.target.value })}
                      data-testid={`new-order-line-qty-${i}`}
                    />
                    <Input
                      type="number" min="0" step="0.01"
                      className="col-span-3 h-9 text-right"
                      placeholder="0.00"
                      value={line.price}
                      onChange={e => updateLine(i, { price: e.target.value })}
                      data-testid={`new-order-line-price-${i}`}
                    />
                    <button type="button" onClick={() => removeLine(i)}
                      disabled={newOrder.lines.length === 1}
                      data-testid={`new-order-line-remove-${i}`}
                      className="col-span-1 inline-flex items-center justify-center rounded-md p-1 text-foreground/40 hover:text-rose-600 disabled:opacity-30">
                      <X className="w-4 h-4"/>
                    </button>
                  </div>
                ))}
                <div className="px-3 py-2.5 flex items-center justify-between bg-secondary/30 text-sm">
                  <span className="text-foreground/60">Order total</span>
                  <span className="display text-lg" data-testid="new-order-total">{money(newOrderTotal)}</span>
                </div>
              </div>
            </div>

            {/* Internal note */}
            <div>
              <label className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 mb-1.5 block">Internal note <span className="text-foreground/40 normal-case">(optional · only you see this)</span></label>
              <Textarea
                rows={2}
                placeholder='e.g. "Phone order from Sarah, Apex Wholesale · trade show booth #42"'
                value={newOrder.note}
                onChange={e => setNewOrder(o => ({ ...o, note: e.target.value }))}
                data-testid="new-order-note"
              />
            </div>
          </div>
          )}

          {orderMode === "bulk" && (
          <div className="mt-4 space-y-3" data-testid="new-order-bulk-pane">
            {!bulkResult && (<>
              <div className="rounded-xl bg-secondary/30 border border-border p-3 text-xs text-foreground/70">
                <div className="font-medium text-foreground mb-1">Paste a spreadsheet</div>
                Required columns: <code className="mono">buyer_email</code>, <code className="mono">name</code>, <code className="mono">qty</code>, <code className="mono">price</code>. Optional: <code className="mono">terms</code> (default Net-30), <code className="mono">note</code>.
                Each row = one line item. Rows with the same buyer + terms + note are grouped into a single order.
                <button type="button" onClick={downloadBulkTemplate} data-testid="new-order-bulk-template"
                  className="ml-1 underline text-foreground hover:text-foreground/70">Download template</button>
              </div>
              <Textarea
                rows={9}
                value={bulkText}
                onChange={(e) => { setBulkText(e.target.value); setBulkParsed(null); }}
                placeholder={`buyer_email,name,qty,price,terms,note\nbuyer@apex.com,"Radiance Serum 30ml · 12-pack",5,42.00,net30,"Trade show booth 42"\nbuyer@apex.com,"Hydra Mask 50ml · 6-pack",2,18.50,net30,"Trade show booth 42"`}
                className="font-mono text-xs"
                data-testid="new-order-bulk-textarea"
              />

              <div className="flex justify-between items-center">
                <Button variant="outline" onClick={() => parseBulk(bulkText)} disabled={!bulkText.trim()}
                  className="rounded-full" data-testid="new-order-bulk-parse">
                  Validate &amp; preview
                </Button>
                {bulkParsed && (
                  <div className="text-xs text-foreground/60">
                    {bulkParsed.rows.length} valid row{bulkParsed.rows.length === 1 ? "" : "s"} · {connectedGroupsCount} order{connectedGroupsCount === 1 ? "" : "s"} ready
                  </div>
                )}
              </div>

              {/* Errors panel: parse errors + unconnected-buyer rows */}
              {bulkParsed && (() => {
                const known = new Set(connections.map(c => (c.buyer_email || "").toLowerCase()));
                const unconnectedRows = [];
                bulkParsed.rows.forEach((r, i) => {
                  if (!known.has(r.buyer_email)) {
                    unconnectedRows.push(`Row ${i + 2} (${r.buyer_email}) — buyer is not connected to your store`);
                  }
                });
                const allErrors = [...bulkParsed.errors, ...unconnectedRows];
                if (allErrors.length === 0) return null;
                return (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 space-y-0.5 max-h-32 overflow-y-auto" data-testid="new-order-bulk-errors">
                    <div className="font-medium mb-1">{allErrors.length} row{allErrors.length === 1 ? "" : "s"} will be skipped</div>
                    {allErrors.map((er, i) => <div key={i} className="mono">• {er}</div>)}
                  </div>
                );
              })()}

              {bulkParsed && bulkParsed.groups.length > 0 && (
                <div className="rounded-xl border border-border divide-y divide-border max-h-72 overflow-y-auto" data-testid="new-order-bulk-preview">
                  <div className="px-3 py-2 text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 bg-secondary/30 sticky top-0">
                    {bulkParsed.groups.length} order{bulkParsed.groups.length === 1 ? "" : "s"} · {bulkParsed.rows.length} line{bulkParsed.rows.length === 1 ? "" : "s"} · {money(bulkParsed.groups.reduce((s, g) => s + g.total, 0))} total
                  </div>
                  {bulkParsed.groups.map((g, i) => {
                    const knownBuyer = connections.some(c => (c.buyer_email || "").toLowerCase() === g.buyer_email);
                    return (
                      <div key={i} className="px-3 py-2.5 text-xs space-y-1" data-testid={`bulk-group-${i}`}>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{g.buyer_email}</span>
                            <span className="chip bg-secondary text-[10px]">{g.terms.toUpperCase()}</span>
                            {!knownBuyer && (
                              <span className="chip bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">
                                <AlertTriangle className="w-3 h-3"/>NOT CONNECTED
                              </span>
                            )}
                          </div>
                          <span className="display text-sm">{money(g.total)}</span>
                        </div>
                        <div className="text-foreground/55 pl-1 space-y-0.5">
                          {g.lines.map((ln, k) => (
                            <div key={k} className="flex justify-between mono">
                              <span className="truncate max-w-[60%]">{ln.name}</span>
                              <span>{ln.qty} × {money(ln.price)}</span>
                            </div>
                          ))}
                        </div>
                        {g.note && <div className="text-foreground/45 italic pl-1">"{g.note}"</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>)}

            {/* Result panel after submission */}
            {bulkResult && (
              <div className="space-y-3" data-testid="new-order-bulk-result">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl border border-border p-3 bg-emerald-50/50">
                    <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Created</div>
                    <div className="display text-2xl mt-1 text-emerald-700">{bulkResult.created_count}</div>
                  </div>
                  <div className="rounded-xl border border-border p-3 bg-rose-50/50">
                    <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Skipped</div>
                    <div className="display text-2xl mt-1 text-rose-700">{bulkResult.failed_count}</div>
                  </div>
                  <div className="rounded-xl border border-border p-3">
                    <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Rows received</div>
                    <div className="display text-2xl mt-1">{bulkResult.row_total}</div>
                  </div>
                </div>
                {bulkResult.created.length > 0 && (
                  <div className="rounded-xl border border-border divide-y divide-border max-h-56 overflow-y-auto">
                    {bulkResult.created.map((o, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex justify-between items-center">
                        <div>
                          <div className="mono font-medium">{o.order_code}</div>
                          <div className="text-foreground/55">{o.buyer_email} · {o.line_count} line{o.line_count === 1 ? "" : "s"} · {o.terms.toUpperCase()}</div>
                        </div>
                        <span className="display">{money(o.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {bulkResult.failed.length > 0 && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 space-y-0.5 max-h-40 overflow-y-auto">
                    <div className="font-medium mb-1">{bulkResult.failed.length} row{bulkResult.failed.length === 1 ? "" : "s"} skipped</div>
                    {bulkResult.failed.map((f, i) => <div key={i} className="mono">• Row {f.row} ({f.buyer_email || "—"}) — {f.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setCreating(false)} className="rounded-full" data-testid="new-order-cancel">
              {bulkResult ? "Done" : "Cancel"}
            </Button>
            {orderMode === "single" && (
              <Button onClick={submitNewOrder} disabled={submittingOrder} data-testid="new-order-submit"
                className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                {submittingOrder ? "Creating…" : "Create & send invoice"}
              </Button>
            )}
            {orderMode === "bulk" && !bulkResult && (
              <Button onClick={submitBulk}
                disabled={submittingBulk || !bulkParsed || connectedGroupsCount === 0}
                data-testid="new-order-bulk-submit"
                className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                {submittingBulk ? "Importing…" : bulkParsed
                  ? (connectedGroupsCount === 0
                      ? "No valid orders to import"
                      : `Create ${connectedGroupsCount} order${connectedGroupsCount === 1 ? "" : "s"}`)
                  : "Validate first"}
              </Button>
            )}
            {orderMode === "bulk" && bulkResult && (
              <Button onClick={() => { setBulkResult(null); setBulkParsed(null); setBulkText(""); }}
                data-testid="new-order-bulk-reset"
                className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                Import more
              </Button>
            )}
          </div>
          <p className="text-[10px] text-center text-foreground/45">
            {orderMode === "single"
              ? "Buyer is notified in-app. Invoice is issued immediately — no separate \"Confirm\" step needed."
              : "Each row = one line item. Rows with the same buyer + terms + note are grouped into a single order."}
          </p>
        </DialogContent>
      </Dialog>

      <AdjustInvoiceModal
        open={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        invoice={adjustTarget}
        onSuccess={() => {
          setAdjustTarget(null);
          load();
        }}
      />

      {/* ── Convert to prebook modal ─────────────────────────────────── */}
      <Dialog open={pbOpen} onOpenChange={setPbOpen}>
        <DialogContent className="max-w-md" data-testid="prebook-convert-dialog">
          <DialogHeader>
            <DialogTitle>Convert to prebook</DialogTitle>
          </DialogHeader>
          {pbOrder && (() => {
            const total = Number(pbOrder.total || 0);
            const deposit = Math.round(total * pbDepositPct) / 100;
            const balance = Math.round((total - deposit) * 100) / 100;
            const promised = new Date(Date.now() + pbLeadWeeks * 7 * 86400e3).toLocaleDateString();
            return (
              <div className="space-y-4">
                <p className="text-[12.5px] text-foreground/65 leading-snug">
                  Set the deposit % and your worst-case lead time. The buyer pays the deposit to activate the order — you commit to ship by the promised date.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11.5px] text-foreground/60 mb-1 block">Deposit %</label>
                    <div className="inline-flex items-center gap-2">
                      <Input
                        type="number" min="1" max="100" step="5"
                        value={pbDepositPct}
                        onChange={e => setPbDepositPct(Number(e.target.value) || 0)}
                        data-testid="prebook-deposit-pct"
                        className="h-10 w-24 mono"
                      />
                      <span className="text-[12px] text-foreground/65">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[11.5px] text-foreground/60 mb-1 block">Lead time (weeks)</label>
                    <div className="inline-flex items-center gap-2">
                      <Input
                        type="number" min="1" max="52"
                        value={pbLeadWeeks}
                        onChange={e => setPbLeadWeeks(Number(e.target.value) || 1)}
                        data-testid="prebook-lead-weeks"
                        className="h-10 w-24 mono"
                      />
                      <span className="text-[12px] text-foreground/65">weeks</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-secondary/30 p-3 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[10px] mono uppercase tracking-widest text-foreground/50">Deposit</div><div className="display text-base mono">{money(deposit)}</div></div>
                  <div><div className="text-[10px] mono uppercase tracking-widest text-foreground/50">Balance</div><div className="display text-base mono">{money(balance)}</div></div>
                  <div><div className="text-[10px] mono uppercase tracking-widest text-foreground/50">Promised</div><div className="text-[13px] font-medium">{promised}</div></div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-900 leading-snug">
                  Once the buyer pays the deposit, you're committed to this date. If you miss it, the buyer can cancel + receive an instant refund. You can request up to 3 extensions.
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" onClick={() => setPbOpen(false)} className="rounded-full" data-testid="prebook-cancel">Cancel</Button>
                  <Button
                    onClick={submitPrebook}
                    disabled={pbSubmitting}
                    data-testid="prebook-submit"
                    className="rounded-full bg-foreground text-background hover:opacity-90 disabled:opacity-40"
                  >
                    {pbSubmitting ? "Sending…" : "Send prebook offer"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Request extension modal ─────────────────────────────────── */}
      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="max-w-md" data-testid="prebook-extension-dialog">
          <DialogHeader>
            <DialogTitle>Request extension</DialogTitle>
          </DialogHeader>
          {extOrder && (() => {
            const pb = extOrder.prebook || {};
            const used = Number(pb.extensions_used || 0);
            return (
              <div className="space-y-4">
                <div className="text-[12.5px] text-foreground/65 leading-snug">
                  Extension <strong>{used + 1} of 3</strong>. The buyer has 10 days to accept — if they don't respond, it auto-accepts.
                </div>
                <div>
                  <label className="text-[11.5px] text-foreground/60 mb-1 block">New ready-by date</label>
                  <Input
                    type="date"
                    value={extDate}
                    onChange={e => setExtDate(e.target.value)}
                    data-testid="prebook-ext-date"
                    className="h-10 mono"
                  />
                </div>
                <div>
                  <label className="text-[11.5px] text-foreground/60 mb-1 block">Reason (shown to buyer)</label>
                  <Textarea
                    rows={3}
                    value={extReason}
                    onChange={e => setExtReason(e.target.value.slice(0, 400))}
                    placeholder="Bottle supplier slipped. New shipment confirmed Aug 22, can pack same day."
                    data-testid="prebook-ext-reason"
                    className="text-[13px]"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setExtOpen(false)} className="rounded-full">Cancel</Button>
                  <Button
                    onClick={submitExtension}
                    disabled={extSubmitting}
                    data-testid="prebook-ext-submit"
                    className="rounded-full bg-foreground text-background hover:opacity-90 disabled:opacity-40"
                  >
                    {extSubmitting ? "Sending…" : "Send extension request"}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function oldestByStage(staged, stage) {
  const s = staged.filter(o => o.stage === stage).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
  return s?.created_at;
}
function daysSince(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

// ── Adjust cell — lights up on the Preparing tab so sellers can fix qty
//    discrepancies discovered during packing. Only renders the button when
//    the linked invoice is adjustable (status ∈ draft|sent|paying|paid AND
//    not fully refunded). Tapping the chip pops AdjustInvoiceModal which
//    then issues a Credit Memo (CRM-####) referenced back to this invoice.
function AdjustOrderCell({ order, onClick }) {
  const inv = order?.invoice;
  if (!inv?.id) {
    return <span className="text-foreground/30 text-xs" title="No invoice yet">—</span>;
  }
  const adjustable = ["draft", "sent", "paying", "paid"].includes(inv.status) && !inv.fully_refunded;
  if (!adjustable) {
    return <span className="text-foreground/30 text-xs" title={`Invoice status: ${inv.status}`}>—</span>;
  }
  const willMoveMoney = ["paid", "paying"].includes(inv.status);
  const cmTotal = Number(inv.credit_memo_total || 0);
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => onClick?.(inv)}
        data-testid={`seller-order-adjust-${order.code}`}
        title={willMoveMoney
          ? "Adjust quantities · money will move to buyer wallet"
          : "Adjust quantities on this invoice"}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-border hover:bg-secondary/40 transition-colors"
      >
        <Pencil className="w-3 h-3 text-foreground/60" />
        <span className="text-[11.5px]">Adjust</span>
        {willMoveMoney && (
          <span className="text-[9px] mono uppercase tracking-[0.12em] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 leading-[14px]" title="Wallet credit will be issued">
            $
          </span>
        )}
      </button>
      {cmTotal > 0 && (
        <span className="text-[10px] mono text-emerald-700" title={`Already credited: $${cmTotal.toFixed(2)}`}>
          −${cmTotal.toFixed(2)}
        </span>
      )}
    </div>
  );
}

// ── Invoice cell — code chip + inline PDF / CSV download ───────────────────
// Lives inside the Orders table; replaces the old "Invoice / Tracking" mash
// so the invoice number is always visible alongside (not instead of) tracking.
function InvoiceCell({ invoice }) {
  if (!invoice?.id) return <span className="text-foreground/30 text-xs">—</span>;
  const dl = async (kind) => {
    try {
      const { data, headers } = await api.get(`/invoices/${invoice.id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || (kind === "pdf" ? "application/pdf" : "text/csv") });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.code || invoice.id}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Could not download ${kind.toUpperCase()}`);
    }
  };
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary border border-border" data-testid={`invoice-cell-${invoice.code}`}>
        <FileText className="w-3 h-3 text-foreground/55"/>
        <span className="font-mono text-[11.5px]">{invoice.code}</span>
        <button
          type="button"
          onClick={() => dl("pdf")}
          title="Download PDF"
          data-testid={`invoice-pdf-${invoice.code}`}
          className="ml-0.5 p-0.5 rounded hover:bg-foreground/10"
        ><Download className="w-3 h-3 text-foreground/60"/></button>
        <button
          type="button"
          onClick={() => dl("csv")}
          title="Download CSV"
          data-testid={`invoice-csv-${invoice.code}`}
          className="px-1 text-[9.5px] mono uppercase tracking-[0.14em] rounded hover:bg-foreground/10 text-foreground/55"
        >CSV</button>
      </div>
      {/* Phase IV — invoice validity countdown on the seller's docs cell so
       *  the seller sees AT a glance which invoices are at risk of auto-
       *  voiding from the buyer's inaction. Hidden for paid/voided/etc. */}
      <InvoiceExpiryChip invoice={invoice} compact/>
    </div>
  );
}

// ── Pack list cell — only lights up after seller enters shipping info
//    (status `transit` / `ready_for_pickup` / `delivered`). PDF stamps the
//    seller's published trade terms + custom T&C so warehouses + buyers
//    can match what was packed against what was billed.
function PackListCell({ order }) {
  if (!order?.id) return <span className="text-foreground/30 text-xs">—</span>;
  const ready = ["ready_for_pickup", "transit", "delivered"].includes(order.status);
  if (!ready) return <span className="text-foreground/30 text-xs" title="Available after shipping info is entered">—</span>;
  const dl = async () => {
    try {
      const { data } = await api.get(`/orders/${order.id}/pack-list/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pack-list-${order.code || order.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download pack list");
    }
  };
  return (
    <button
      type="button"
      onClick={dl}
      title="Download pack list PDF"
      data-testid={`pack-list-pdf-${order.code}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary border border-border hover:bg-secondary/80"
    >
      <Package className="w-3 h-3 text-foreground/55"/>
      <span className="font-mono text-[11.5px]">PL-{(order.code || "").replace(/^ORD-/, "")}</span>
      <Download className="w-3 h-3 text-foreground/60 ml-0.5"/>
    </button>
  );
}

// ── Tracking cell — number + carrier chip + copy-to-clipboard ──────────────
function TrackingCell({ shipment }) {
  if (!shipment?.tracking) return <span className="text-foreground/30 text-xs">—</span>;
  const tracking = shipment.tracking;
  const carrier = (shipment.carrier || "").toUpperCase();
  const display = tracking.length > 18 ? tracking.slice(0, 14) + "…" : tracking;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(tracking);
      toast.success("Tracking copied");
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary border border-border" data-testid={`tracking-cell-${tracking}`}>
      <Truck className="w-3 h-3 text-foreground/55"/>
      <span className="font-mono text-[11.5px]" title={tracking}>{display}</span>
      {carrier && (
        <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1 py-px rounded-sm bg-foreground/10">{carrier}</span>
      )}
      <button
        type="button"
        onClick={copy}
        title="Copy tracking"
        data-testid={`tracking-copy-${tracking}`}
        className="ml-0.5 p-0.5 rounded hover:bg-foreground/10"
      ><Copy className="w-3 h-3 text-foreground/60"/></button>
    </div>
  );
}


// ── Documents cell — collapses Invoice + Pack list + Tracking into one
// flex-row of conditional chips. Each chip only renders if the underlying
// doc actually exists; if NO docs exist for the row, returns a single em-dash.
// Saves 2 columns of horizontal real estate vs. the previous 3-column layout.
// Pass `omitTracking` (used on the Delivered tab) to keep the Documents cell
// purely about static docs + render Tracking as its own dedicated column.
function DocumentsCell({ order, omitTracking = false }) {
  const hasInvoice  = !!order?.invoice?.id;
  const hasPackList = !!order?.id && ["ready_for_pickup", "transit", "delivered"].includes(order.status);
  const hasTracking = !omitTracking && !!order?.shipment?.tracking;
  if (!hasInvoice && !hasPackList && !hasTracking) {
    return <span className="text-foreground/30 text-xs" title="Documents appear once the order is confirmed and packed">—</span>;
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`docs-cell-${order.code}`}>
      {hasInvoice  && <InvoiceCell invoice={order.invoice}/>}
      {hasPackList && <PackListCell order={order}/>}
      {hasTracking && <TrackingCell shipment={order.shipment}/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SampleOrdersPreview — empty-state preview for new sellers. Shows 3 sample
// orders matching the buyer/order/invoice/shipment data network. Click any
// row → opens a fully rendered <SampleOrderDrawer> with hardcoded fake data.
// Disappears the moment the seller has ≥ 1 real order.
// ─────────────────────────────────────────────────────────────────────────────
function SampleOrdersPreview() {
  const [sampleOrder, setSampleOrder] = useState(null);
  return (
    <div data-testid="sample-orders-preview" className="text-left">
      <SamplePreviewBanner noun="Orders"/>
      <div className="divide-y divide-border">
        {SAMPLE_ORDERS.map(o => (
          <SampleOrderRow key={o.id} sample={o} onOpen={() => setSampleOrder(o)} />
        ))}
      </div>
      <SampleOrderDrawer order={sampleOrder} viewerRole="seller" onClose={() => setSampleOrder(null)} />
    </div>
  );
}

function SampleOrderRow({ sample, onOpen }) {
  const tone =
    sample.status === "delivered"  ? "bg-emerald-500/15 text-emerald-700" :
    sample.status === "in_transit" ? "bg-blue-500/15 text-blue-700" :
    sample.status === "processing" ? "bg-amber-500/15 text-amber-800" :
                                      "bg-foreground/10 text-foreground/65";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full grid grid-cols-[1fr_1.4fr_120px_90px_110px_140px_120px] gap-3 px-5 py-3 items-center opacity-[0.78] hover:opacity-100 hover:bg-secondary/40 transition-all"
      data-testid={`sample-order-row-${sample.id}`}
      aria-label={`Sample order - ${sample.code}`}
    >
      <div className="font-mono text-[12.5px] text-slate-700 flex items-center gap-1.5">
        {sample.code}
        <SampleChip />
      </div>
      <div className="text-[13px] text-slate-700 truncate">{sample.buyer_name}</div>
      <div>
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${tone}`}>
          {sample.status.replace(/_/g, " ")}
        </span>
      </div>
      <div className="text-[12px] text-slate-600">{sample.units} units</div>
      <div className="text-[13px] font-medium text-slate-700 tabular-nums">${sample.total.toLocaleString()}</div>
      <div className="text-[11.5px] text-slate-500">{sample.created_at}</div>
      <div className="text-right text-[11px] text-foreground/40">Open →</div>
    </button>
  );
}

